import { useWelcomeOffer, WelcomePrice } from '../offers/WelcomeOfferContext';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../api/dashboardService';
import { jobService } from '../api/jobService';
import { userService } from '../api/userService';
import { subscriptionService } from '../api/subscriptionService';
import { ProjectGridSkeleton } from '../components/Skeleton';
import OnboardingModal from '../components/OnboardingModal';
import PhoneVerificationModal from '../components/PhoneVerificationModal';
import TimeSavedCard, { isTimeSavedSignificant } from './TimeSavedCard';
import DashboardTour, { TourTriggerButton, shouldShowTour, ProjectCreationTour, shouldShowProjectTour, dismissProjectTour } from '../components/DashboardTour';
import { useAuth } from '../auth/AuthContext';
import WhatsAppFloatingButton from '../components/WhatsAppFloatingButton';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useI18n, translate, rich } from '../i18n';
import PartnerOffersBanner from '../components/PartnerOffersBanner';
import PremiereDashboardCard from '../components/premiere/PremiereDashboardCard';
import { getDisplayCurrency, getAllPlanPrices, getCachedCountry } from '../utils/pricing';
import {
  AUTO_ARABIC,
  ALL_ARABIC_DIALECTS,
  getDialectSubset,
  getPrimaryDialectId,
  getDialectById,
  resolveGeoCountry,
  applyGeoOverrideFromUrl,
} from '../utils/geoDialects';

// Example audio files for users to try
const EXAMPLE_AUDIOS = [
  // {
  //   id: 'example-1',
  //   name: 'Arabcap Sample 1',
  //   gcsUri: 'gs://arabcap-storage/uploads/63c842ba-3e4c-4954-ae7a-d6c761192e15.mp3', // Replace with actual GCS URI
  //   audioUrl: 'https://storage.googleapis.com/storage-annarabic/arabcap_50c433bf-f2d4-4ddb-818b-1bbe7b57ecbb.mp3', // Replace with actual audio URL for preview
  //   duration: 37, // Duration in seconds
  //   size: 2.1, // Size in MB
  //   dialect: '🇲🇦 Darija Mixed with French' // Visual indicator
  // },
  // {
  //   id: 'example-2',
  //   name: 'Arabcap Sample 2',
  //   gcsUri: 'gs://arabcap-storage/uploads/b7ccb531-880e-48f0-ad56-0691bd265c15.mp3', // Replace with actual GCS URI
  //   audioUrl: 'https://storage.googleapis.com/storage-annarabic/arabcap_5b990c50-a007-47c9-b73e-e6cea44c30bb.mp3', // Replace with actual audio URL for preview
  //   duration: 38, // Duration in seconds
  //   size: 1.8, // Size in MB
  //   dialect: '🇲🇦 Darija Mixed with French' // Visual indicator
  // }
  {
    id: 'example-1',
    name: 'Arabcap Sample 1',
    gcsUri: 'gs://arabcap-storage/uploads/c551c842-9abe-409d-ae38-5bb1173f8e7b.mp3', // Replace with actual GCS URI
    audioUrl: 'https://storage.googleapis.com/storage-annarabic/Arabcap-Example.mp3', // Replace with actual audio URL for preview
    duration: 30, // Duration in seconds
    size: 1.8, // Size in MB
    dialect: '🇲🇦 Darija Mixed with French' // Visual indicator
  }
];

const MOCK_PROJECTS = [
  {
    id: 'p1',
    name: 'Vlog Casablanca - Day 1',
    status: 'COMPLETED',
    createdAt: '2023-10-25T14:30:00Z',
    duration: '10:24',
    thumbnail: 'https://picsum.photos/400/225?random=1'
  },
  {
    id: 'p2',
    name: 'Podcast Episode 4 - Tech Talk',
    status: 'DRAFT',
    createdAt: '2023-10-26T09:15:00Z',
    duration: '45:00',
    thumbnail: 'https://picsum.photos/400/225?random=2'
  },
  {
    id: 'p3',
    name: 'Instagram Reel - Food Review',
    status: 'PROCESSING',
    createdAt: '2023-10-27T18:45:00Z',
    duration: '01:30',
    thumbnail: 'https://picsum.photos/400/225?random=3'
  },
  {
    id: 'p4',
    name: 'YouTube Short - Darija Tips',
    status: 'COMPLETED',
    createdAt: '2023-10-28T11:05:00Z',
    duration: '00:52',
    thumbnail: 'https://picsum.photos/400/225?random=4'
  },
  {
    id: 'p5',
    name: 'Client Ad - Riyad Promo',
    status: 'DRAFT',
    createdAt: '2023-10-30T08:40:00Z',
    duration: '02:10',
    thumbnail: 'https://picsum.photos/400/225?random=5'
  },
  {
    id: 'p6',
    name: 'Behind The Scenes - Studio',
    status: 'COMPLETED',
    createdAt: '2023-10-31T16:10:00Z',
    duration: '07:18',
    thumbnail: 'https://picsum.photos/400/225?random=6'
  },
  {
    id: 'p7',
    name: 'TikTok Trends - Week 2',
    status: 'PROCESSING',
    createdAt: '2023-11-01T13:05:00Z',
    duration: '00:39',
    thumbnail: 'https://picsum.photos/400/225?random=7'
  },
  {
    id: 'p8',
    name: 'Interview - Creator Spotlight',
    status: 'DRAFT',
    createdAt: '2023-11-02T09:55:00Z',
    duration: '12:01',
    thumbnail: 'https://picsum.photos/400/225?random=8'
  },
  {
    id: 'p9',
    name: 'Marrakech Travel Guide',
    status: 'COMPLETED',
    createdAt: '2023-11-03T10:20:00Z',
    duration: '15:42',
    thumbnail: 'https://picsum.photos/400/225?random=9'
  },
  {
    id: 'p10',
    name: 'Cooking Show - Tagine Recipe',
    status: 'COMPLETED',
    createdAt: '2023-11-04T14:15:00Z',
    duration: '08:33',
    thumbnail: 'https://picsum.photos/400/225?random=10'
  },
  {
    id: 'p11',
    name: 'Street Art Documentary',
    status: 'DRAFT',
    createdAt: '2023-11-05T11:45:00Z',
    duration: '22:17',
    thumbnail: 'https://picsum.photos/400/225?random=11'
  },
  {
    id: 'p12',
    name: 'Music Video - Gnawa Fusion',
    status: 'COMPLETED',
    createdAt: '2023-11-06T16:30:00Z',
    duration: '04:28',
    thumbnail: 'https://picsum.photos/400/225?random=12'
  },
  {
    id: 'p13',
    name: 'Tech Review - iPhone 15',
    status: 'PROCESSING',
    createdAt: '2023-11-07T09:00:00Z',
    duration: '11:55',
    thumbnail: 'https://picsum.photos/400/225?random=13'
  },
  {
    id: 'p14',
    name: 'Fitness Routine - Morning Workout',
    status: 'COMPLETED',
    createdAt: '2023-11-08T07:30:00Z',
    duration: '18:20',
    thumbnail: 'https://picsum.photos/400/225?random=14'
  },
  {
    id: 'p15',
    name: 'Language Learning - Darija Basics',
    status: 'DRAFT',
    createdAt: '2023-11-09T13:45:00Z',
    duration: '25:10',
    thumbnail: 'https://picsum.photos/400/225?random=15'
  },
  {
    id: 'p16',
    name: 'Coffee Shop Tour - Rabat',
    status: 'COMPLETED',
    createdAt: '2023-11-10T15:20:00Z',
    duration: '06:45',
    thumbnail: 'https://picsum.photos/400/225?random=16'
  },
  {
    id: 'p17',
    name: 'Gaming Stream Highlights',
    status: 'PROCESSING',
    createdAt: '2023-11-11T20:00:00Z',
    duration: '32:18',
    thumbnail: 'https://picsum.photos/400/225?random=17'
  },
  {
    id: 'p18',
    name: 'Fashion Haul - Summer Collection',
    status: 'COMPLETED',
    createdAt: '2023-11-12T12:10:00Z',
    duration: '14:55',
    thumbnail: 'https://picsum.photos/400/225?random=18'
  },
  {
    id: 'p19',
    name: 'Podcast Episode 5 - Entrepreneurship',
    status: 'DRAFT',
    createdAt: '2023-11-13T08:30:00Z',
    duration: '52:30',
    thumbnail: 'https://picsum.photos/400/225?random=19'
  },
  {
    id: 'p20',
    name: 'Wedding Highlights - Fes',
    status: 'COMPLETED',
    createdAt: '2023-11-14T18:45:00Z',
    duration: '09:12',
    thumbnail: 'https://picsum.photos/400/225?random=20'
  },
  {
    id: 'p21',
    name: 'Product Launch - Skincare Line',
    status: 'DRAFT',
    createdAt: '2023-11-15T10:00:00Z',
    duration: '03:45',
    thumbnail: 'https://picsum.photos/400/225?random=21'
  },
  {
    id: 'p22',
    name: 'Day in My Life - Content Creator',
    status: 'COMPLETED',
    createdAt: '2023-11-16T14:30:00Z',
    duration: '16:28',
    thumbnail: 'https://picsum.photos/400/225?random=22'
  }
];

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="7" />
    <line x1="20" y1="20" x2="16.5" y2="16.5" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ArrowRightIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5L12 2z" />
  </svg>
);

const normalizeJobStatus = (status) => {
  if (!status) return 'DRAFT';
  return String(status).toUpperCase();
};

// Format duration in seconds to MM:SS or HH:MM:SS
const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return null;
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const mapJobToProject = (job, index = 0) => {
  const id = job?.id || job?.job_id || job?._id || `job-${index}`;
  const name =
    job?.name ||
    job?.project_name ||
    job?.filename ||
    job?.original_filename ||
    translate('Untitled Project');
  const createdAt = job?.created_at || job?.createdAt || job?.created || new Date().toISOString();
  // Handle both formatted duration and duration_seconds
  const durationSeconds = job?.duration_seconds || job?.media_duration;
  const duration = job?.duration || (durationSeconds ? formatDuration(durationSeconds) : null);
  const thumbnail = job?.thumbnail_url || job?.thumbnail || null;
  const progress = job?.progress;
  const currentStep = job?.current_step;

  return {
    id,
    name,
    status: normalizeJobStatus(job?.status || job?.state || job?.job_status),
    createdAt,
    duration,
    durationSeconds,
    thumbnail,
    progress,
    currentStep,
    raw: job
  };
};

const extractJobsResponse = (data) => {
  const payload = data || {};
  const jobs =
    payload.items ||
    payload.jobs ||
    payload.results ||
    payload.data ||
    payload.records ||
    [];
  const total =
    payload.total ||
    payload.count ||
    payload.total_count ||
    payload.totalCount ||
    jobs.length;
  return { jobs, total };
};

const ProjectCard = ({ project, onOpen }) => {
  const { t, lang, formatDate } = useI18n();
  const isProcessing = ['PROCESSING', 'IN_PROGRESS', 'QUEUED', 'RUNNING', 'PENDING', 'CONVERTING', 'TRANSCRIBING'].includes(project.status);
  const isFailed = project.status === 'FAILED';
  const stepLabels = {
    converter: 'Converting media',
    audiolm: 'Transcribing audio',
    soniox: 'Refining output',
    insights: 'Finalizing',
    completed: 'Completed'
  };
  const currentStepLabel = project.currentStep
    ? (stepLabels[project.currentStep] || project.currentStep)
    : (project.status === 'CONVERTING' ? 'Converting media' : project.status === 'TRANSCRIBING' ? 'Transcribing audio' : null);
  
  const handleClick = () => {
    // Don't allow opening failed or processing jobs
    if (isFailed || isProcessing) return;
    onOpen();
  };
  
  return (
    <div
      role="button"
      tabIndex={isFailed || isProcessing ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      className={`group bg-white rounded-[24px] border shadow-sm transition-all duration-300 flex flex-col overflow-hidden relative text-start ${
        isFailed 
          ? 'border-slate-200 cursor-not-allowed opacity-80' 
          : isProcessing
            ? 'border-slate-100 cursor-wait'
            : 'border-slate-100 hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.08)] hover:-translate-y-1 cursor-pointer'
      }`}
    >
      <div className="p-3 pb-0">
        <div className="relative aspect-[16/10] bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 group-hover:border-slate-200 transition-colors">
          {project.thumbnail ? (
            <img
              src={project.thumbnail}
              alt={project.name}
              className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
                !isFailed && !isProcessing ? 'group-hover:scale-105' : ''
              }`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-100">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-slate-300">
                <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 9.5v5l4-2.5-4-2.5z" fill="currentColor"/>
              </svg>
            </div>
          )}
          <div className="absolute bottom-3 end-3 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-lg border border-white/10 shadow-lg">
            {project.duration || '00:00'}
          </div>
          {isProcessing && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] flex flex-col items-center justify-center">
              {/* Circular progress indicator */}
              <div className="relative w-14 h-14 mb-2">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 48 48">
                  {/* Background circle */}
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="4"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    fill="none"
                    stroke="#f73140"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - (project.progress || 0) / 100)}`}
                    className="transition-all duration-500 ease-out"
                  />
                </svg>
                {/* Percentage in center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-slate-700">
                    {project.progress != null ? `${project.progress}%` : '...'}
                  </span>
                </div>
              </div>
              {currentStepLabel && (
                <span className="text-[10px] font-semibold text-slate-500">{t(currentStepLabel)}</span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-1.5">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h3 className={`font-bold text-[15px] leading-snug line-clamp-1 transition-colors ${
              isFailed 
                ? 'text-red-900/70' 
                : 'text-slate-900 group-hover:text-[#f73140]'
            }`}>
              {project.name}
            </h3>
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400">
            <span>{lang === 'ar' ? formatDate(project.createdAt, { day: 'numeric', month: 'short' }) : new Date(project.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
          </div>
          {project.status === 'DRAFT' && (
            <span className="text-[9px] font-extrabold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-md uppercase tracking-wide">
              {t('Brouillon')}
            </span>
          )}
          {isFailed && (
            <span className="text-[9px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-md border border-red-200 uppercase tracking-wide">
              {t('Failed')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper to get cached usage data from localStorage
const getCachedUsageData = () => {
  try {
    const cached = localStorage.getItem('arabcap_usage_data');
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// Helper to get cached plan from localStorage
const getCachedPlan = () => {
  try {
    return localStorage.getItem('arabcap_user_plan') || 'free';
  } catch {
    return 'free';
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, setUserState } = useAuth();
  const { t, isRTL, locale } = useI18n();
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userPlan, setUserPlan] = useState(getCachedPlan); // Initialize from cache
  const [usageData, setUsageData] = useState(getCachedUsageData); // Initialize from cache
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const [jobsTotal, setJobsTotal] = useState(0);

  // Onboarding modal state
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Dashboard tour state
  const [showTour, setShowTour] = useState(false);
  const [showProjectTour, setShowProjectTour] = useState(false);
  const [lastDismissedStep, setLastDismissedStep] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [animKey, setAnimKey] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('Preparing upload...');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const dashboardStreamRefs = useRef({}); // Track SSE connections for dashboard jobs

  // New project flow state
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const userPickedLanguageRef = useRef(false);
  // Dialect chosen in step 2 — kept so switching Arabizi → Standard can restore it
  const dialectBeforeArabiziRef = useRef(null);
  const [scriptStyle, setScriptStyle] = useState('standard');
  const [foreignWordsMode, setForeignWordsMode] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedExample, setSelectedExample] = useState(null); // For example audios
  const [isUsingExample, setIsUsingExample] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null); // For audio preview
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);
  
  // Advanced chunk settings
  const [chunkMode, setChunkMode] = useState('words'); // 'words' or 'characters'
  const [wordsPerChunk, setWordsPerChunk] = useState(3);
  const [charsPerChunk, setCharsPerChunk] = useState(50);
  const [separateForeignChunks, setSeparateForeignChunks] = useState(true);
  
  // Split settings for phrase generation
  const [splitOnPunctuation, setSplitOnPunctuation] = useState(true);
  const [splitOnSilence, setSplitOnSilence] = useState(true);

  // Duration check and upload state
  const [fileDuration, setFileDuration] = useState(null);
  const [durationChecking, setDurationChecking] = useState(false);
  const [durationError, setDurationError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, completed, error
  const [presignedData, setPresignedData] = useState(null);
  const uploadPromiseRef = useRef(null);

  // Processing progress state
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStep, setProcessingStep] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);

  // Manifesto modal
  const [showManifesto, setShowManifesto] = useState(false);
  const [manifestoStep, setManifestoStep] = useState(1);
  const totalManifestoSteps = 5;

  // Mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarClosing, setSidebarClosing] = useState(false);
  const [sidebarAnimating, setSidebarAnimating] = useState(false);

  // Handle sidebar close with animation
  const handleCloseSidebar = () => {
    setSidebarClosing(true);
    setTimeout(() => {
      setSidebarOpen(false);
      setSidebarClosing(false);
    }, 300); // Match animation duration
  };

  // Trigger slide-in animation when sidebar opens
  useEffect(() => {
    if (sidebarOpen && !sidebarClosing) {
      // Small delay to ensure DOM is ready for animation
      const timer = setTimeout(() => setSidebarAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setSidebarAnimating(false);
    }
  }, [sidebarOpen, sidebarClosing]);

  // Limit reached modal
  const [showLimitReached, setShowLimitReached] = useState(false);

  // Billing/Upgrade state
  const [billingPlans, setBillingPlans] = useState([]);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);

  // Upgrade modal (for trial users)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // "Time saved with ArabCap" card (total length of completed videos)
  const [timeSaved, setTimeSaved] = useState(null); // card renders only once loaded
  const showTimeSaved = !!timeSaved && isTimeSavedSignificant({
    videoSeconds: timeSaved.video_seconds,
    videos: timeSaved.completed_videos,
  });

  useEffect(() => {
    let cancelled = false;
    dashboardService.getStats()
      .then((stats) => {
        if (!cancelled && stats?.time_saved) setTimeSaved(stats.time_saved);
      })
      .catch(() => { /* stats unavailable: the card stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  // Phone verification required by the fraud check at upload time
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const retryAfterVerificationRef = useRef(false);

  useEffect(() => {
    const handleVerificationRequired = () => setShowVerificationModal(true);
    window.addEventListener('verification-required', handleVerificationRequired);
    return () => window.removeEventListener('verification-required', handleVerificationRequired);
  }, []);

  // Geo / billing currency for plan price display + checkout
  // ?geo=ma | ?geo=JO | ?geo=world overrides and persists (same idea as landing page)
  const [geoCountry, setGeoCountry] = useState(() => {
    const fromUrl = applyGeoOverrideFromUrl();
    if (fromUrl !== undefined) return fromUrl;
    return getCachedCountry();
  });
  const [pricingCurrency, setPricingCurrency] = useState(() =>
    getDisplayCurrency({
      geoCountry: (() => {
        const fromUrl = applyGeoOverrideFromUrl();
        if (fromUrl !== undefined) return fromUrl;
        return getCachedCountry();
      })(),
    })
  );
  const { offer: welcomeOffer } = useWelcomeOffer();
  const planPrices = getAllPlanPrices(pricingCurrency);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [detectedCountry, sub] = await Promise.all([
        resolveGeoCountry(),
        subscriptionService.getMySubscription().catch(() => null),
      ]);
      if (cancelled) return;
      // null is valid (?geo=world); only skip when resolve failed entirely
      if (detectedCountry !== undefined) setGeoCountry(detectedCountry);
      setPricingCurrency(getDisplayCurrency({
        billingCurrency: sub?.billing_currency,
        geoCountry: detectedCountry,
      }));
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingJobs(true);
    setJobsError(null);

    dashboardService
      .getJobs(currentPage, itemsPerPage)
      .then((data) => {
        if (!isMounted) return;
        const { jobs, total } = extractJobsResponse(data);
        setProjects(jobs.map((job, index) => mapJobToProject(job, index)));
        setJobsTotal(total);
      })
      .catch((err) => {
        if (!isMounted) return;
        setJobsError(err?.message || 'Failed to load jobs.');
        setProjects(MOCK_PROJECTS.map((job, index) => mapJobToProject(job, index)));
        setJobsTotal(MOCK_PROJECTS.length);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingJobs(false);
      });

    return () => {
      isMounted = false;
    };
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    return () => {
      // Cleanup modal progress polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Cleanup dashboard job streams
      Object.values(dashboardStreamRefs.current).forEach(es => es?.close());
      dashboardStreamRefs.current = {};
    };
  }, []);

  // Auto-start tour for first-time users (after jobs are loaded)
  useEffect(() => {
    // Only auto-start if jobs are loaded and tour should show
    if (!isLoadingJobs && !showOnboarding && shouldShowTour()) {
      // Small delay to let the UI settle
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [isLoadingJobs, showOnboarding]);

  // Fetch fresh profile and check if user needs to complete onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        // Fetch fresh profile from API to get latest onboarding status
        const freshProfile = await userService.getMe();
        if (freshProfile) {
          // Update auth context with fresh data
          if (setUserState) {
            setUserState(freshProfile);
          }
          
          // Check if onboarding is needed
          if (!freshProfile.onboarding_completed) {
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch profile for onboarding check:', error);
      }
    };

    // Only check when component mounts
    checkOnboarding();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle phone number submission (immediate, independent from survey)
  const handlePhoneSubmit = useCallback(async (phoneNumber) => {
    try {
      await userService.updatePhoneNumber(phoneNumber);
      console.log('Phone number saved successfully');
    } catch (error) {
      console.error('Failed to save phone number:', error);
      // Don't throw - we still want to continue with the survey
    }
  }, []);

  // Handle onboarding survey completion
  const handleOnboardingComplete = useCallback(async (answers) => {
    try {
      const updatedProfile = await userService.completeOnboarding(answers);
      if (updatedProfile && setUserState) {
        // Update user in auth context
        setUserState(updatedProfile);
      }
      setShowOnboarding(false);
      // Show tour after onboarding if applicable
      if (shouldShowTour()) {
        setTimeout(() => setShowTour(true), 500);
      }
    } catch (error) {
      console.error('Failed to save onboarding data:', error);
      // Still close the modal on error to not block the user
      setShowOnboarding(false);
    }
  }, [setUserState]);

  // Track whether we have processing jobs for SSE streaming
  const processingStatuses = ['PROCESSING', 'IN_PROGRESS', 'QUEUED', 'RUNNING', 'PENDING', 'CONVERTING', 'TRANSCRIBING'];
  const processingJobs = projects.filter(p => processingStatuses.includes(p.status));
  
  // Connect to SSE streams for processing jobs (real-time progress updates)
  useEffect(() => {
    // Get current processing job IDs
    const processingJobIds = new Set(processingJobs.map(p => p.id));
    
    // Close streams for jobs that are no longer processing
    Object.keys(dashboardStreamRefs.current).forEach(jobId => {
      if (!processingJobIds.has(jobId)) {
        dashboardStreamRefs.current[jobId]?.close();
        delete dashboardStreamRefs.current[jobId];
      }
    });
    
    // Create streams for new processing jobs
    processingJobs.forEach(job => {
      // Skip if already connected
      if (dashboardStreamRefs.current[job.id]) return;
      
      // Create SSE connection for this job
      const eventSource = jobService.streamProgress(job.id, (event) => {
        try {
          const payload = JSON.parse(event.data);
          const step = payload?.step;
          const percent = payload?.percent;
          const eventType = payload?.event;
          
          // Update the project in state with new progress
          setProjects(prev => prev.map(p => {
            if (p.id !== job.id) return p;
            return {
              ...p,
              progress: percent !== undefined ? percent : p.progress,
              currentStep: step || p.currentStep,
            };
          }));
          
          // Handle completion - close stream and refresh data
          if (eventType === 'completed' || payload?.done) {
            dashboardStreamRefs.current[job.id]?.close();
            delete dashboardStreamRefs.current[job.id];
            
            // Refresh job list to get final status
            dashboardService.getJobs(currentPage, itemsPerPage).then((data) => {
              const { jobs, total } = extractJobsResponse(data);
              setProjects(jobs.map((j, index) => mapJobToProject(j, index)));
              setJobsTotal(total);
            });
          }
          
          // Handle error
          if (eventType === 'error') {
            dashboardStreamRefs.current[job.id]?.close();
            delete dashboardStreamRefs.current[job.id];
            
            // Refresh job list to get error status
            dashboardService.getJobs(currentPage, itemsPerPage).then((data) => {
              const { jobs, total } = extractJobsResponse(data);
              setProjects(jobs.map((j, index) => mapJobToProject(j, index)));
              setJobsTotal(total);
            });
          }
        } catch (error) {
          // Ignore parse errors
        }
      });
      
      // Handle SSE errors
      eventSource.onerror = () => {
        dashboardStreamRefs.current[job.id]?.close();
        delete dashboardStreamRefs.current[job.id];
      };
      
      dashboardStreamRefs.current[job.id] = eventSource;
    });
    
    // Cleanup on unmount
    return () => {
      Object.values(dashboardStreamRefs.current).forEach(es => es?.close());
      dashboardStreamRefs.current = {};
    };
  }, [processingJobs.map(p => p.id).join(','), currentPage, itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch user plan and usage data from API
  useEffect(() => {
    userService.getUsage()
      .then((data) => {
        if (data) {
          const newPlan = data.plan || 'free';
          
          // Only update plan if different from current
          setUserPlan(prevPlan => {
            if (prevPlan !== newPlan) {
              try {
                localStorage.setItem('arabcap_user_plan', newPlan);
              } catch { /* ignore */ }
              return newPlan;
            }
            return prevPlan;
          });
          
          // Update usage data and cache it
          setUsageData(prevData => {
            // Always update usage data as limits/usage may change
            try {
              localStorage.setItem('arabcap_usage_data', JSON.stringify(data));
            } catch { /* ignore */ }
            return data;
          });
        }
      })
      .catch((err) => {
        console.error('Failed to fetch usage data:', err);
        // Keep cached data on error (already initialized from localStorage)
      });
  }, []);

  // Format renewal date as "Renews 3 Nov"
  const formatRenewalDate = (isoDate) => {
    if (!isoDate) return null;
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return null;
      const day = date.getDate();
      const month = date.toLocaleString(locale, { month: 'short' });
      return t('Renews {date}', { date: `${day} ${month}` });
    } catch {
      return null;
    }
  };

  // Soonest-expiring bonus credits as "2 expire 24 Feb" (bonus_expiring is sorted soonest first)
  const nextBonusExpiry = usageData?.bonus_expiring?.[0];
  const bonusExpiryNote = nextBonusExpiry
    ? t('{count} expire {date}', {
        count: nextBonusExpiry.amount,
        date: new Date(nextBonusExpiry.expires_at).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
      })
    : null;

  // Get reset date from subscription period end as "Resets 24 Feb"
  const getResetDate = () => {
    // Use current_period_end from usage data if available
    if (usageData?.current_period_end) {
      try {
        const date = new Date(usageData.current_period_end);
        if (!isNaN(date.getTime())) {
          const day = date.getDate();
          const month = date.toLocaleString(locale, { month: 'short' });
          return t('Resets {date}', { date: `${day} ${month}` });
        }
      } catch {
        // Fall through to default
      }
    }
    // Fallback: calculate 1 month from now
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
    const day = nextMonth.getDate();
    const month = nextMonth.toLocaleString(locale, { month: 'short' });
    return t('Resets {date}', { date: `${day} ${month}` });
  };


  // Handle upgrade to specific plan
  const handleUpgradeToPlan = async (plan) => {
    try {
      setUpgradeLoading(true);
      setBillingError(null);
      
      // If user already has a paid plan (starter, pro, or max), redirect to Stripe portal to manage/upgrade
      if (userPlan === 'starter' || userPlan === 'pro' || userPlan === 'max') {
        const result = await subscriptionService.openPortal();
        if (result?.portal_url) {
          window.location.href = result.portal_url;
        } else {
          setBillingError('Failed to open billing portal');
        }
      } else {
        // Trial users: create new checkout session
        const result = await subscriptionService.createCheckoutSession(plan, pricingCurrency, welcomeOffer?.plans.includes(plan) ? welcomeOffer.percent_off : null);
        if (result?.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          setBillingError('Failed to create checkout session');
        }
      }
    } catch (err) {
      console.error('Error processing upgrade:', err);
      setBillingError('Failed to process upgrade. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Handle upgrade to Pro (legacy)
  const handleUpgrade = async () => {
    await handleUpgradeToPlan('pro');
  };

  // Handle manage subscription (Stripe portal)
  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      setBillingError(null);
      const result = await subscriptionService.openPortal();
      if (result?.portal_url) {
        window.location.href = result.portal_url;
      } else {
        setBillingError('Failed to open billing portal');
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      setBillingError('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalRecords = searchQuery ? filteredProjects.length : (jobsTotal || projects.length);
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const currentProjects = searchQuery ? filteredProjects : projects;

  const openNewProjectModal = (fromTour = false) => {
    // Check if user has reached their video limit
    if (usageData?.limits?.videos_remaining !== undefined && usageData.limits.videos_remaining <= 0) {
      setShowLimitReached(true);
      return;
    }

    setModalStep(1);
    setSelectedFile(null);
    setSelectedExample(null);
    setIsUsingExample(false);
    userPickedLanguageRef.current = false;
    dialectBeforeArabiziRef.current = null;
    setSelectedLanguage(null);
    setScriptStyle('standard');
    setForeignWordsMode(null);
    setProjectName('');
    setShowAdvanced(false);
    setChunkMode('words');
    setWordsPerChunk(3);
    setCharsPerChunk(50);
    setSeparateForeignChunks(true);
    setIsCreatingProject(false);
    setCreateError(null);
    setProcessingStatus('Preparing upload...');
    // Reset duration and upload state
    setFileDuration(null);
    setDurationChecking(false);
    setDurationError(null);
    setUploadStatus('idle');
    setPresignedData(null);
    uploadPromiseRef.current = null;
    // Reset processing progress state
    setProcessingProgress(5);
    setProcessingStep(null);
    setCurrentJobId(null);
    setIsModalOpen(true);
    
    // Reset guide dismissed state so it shows on all steps
    setLastDismissedStep(null);
    
    // Show project creation guide if coming from dashboard tour and not permanently dismissed
    if (fromTour && shouldShowProjectTour()) {
      setTimeout(() => setShowProjectTour(true), 300);
    }
  };
  
  // Handler for when tour triggers new project
  const handleTourNewProjectClick = () => {
    setShowTour(false);
    setTimeout(() => openNewProjectModal(true), 100);
  };

  const closeNewProjectModal = () => {
    if (isCreatingProject || modalStep === 6) return;
    setIsModalOpen(false);
  };

  // Duration extraction utility
  const getMediaDuration = (file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const media = file.type.startsWith('audio/') 
        ? new Audio() 
        : document.createElement('video');
      media.preload = 'metadata';
      media.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(media.duration);
      };
      media.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null); // Duration unknown - allow to proceed, backend validates
      };
      media.src = url;
    });
  };

  // Format duration in seconds to MM:SS or HH:MM:SS
  const formatDurationSeconds = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Audio player controls
  const playAudio = (audio) => {
    setPreviewAudio(audio);
    setIsPlaying(true);
    setCurrentTime(0);
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const closeAudioPlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPreviewAudio(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setAudioDuration(0);
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const progressBar = e.currentTarget;
    const clickPosition = e.nativeEvent.offsetX;
    const progressBarWidth = progressBar.offsetWidth;
    const newTime = (clickPosition / progressBarWidth) * audioDuration;
    
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Auto-play when preview audio changes
  useEffect(() => {
    if (previewAudio && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [previewAudio]);


  // Silent background upload (no UI feedback to user) with retry logic
  const startSilentUpload = async (file) => {
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        setUploadStatus('uploading');
        
        // Get presigned URL
        const contentType = file.type || 'application/octet-stream';
        const presigned = await jobService.getPresignedUrl(file.name, contentType);
        setPresignedData(presigned);
        
        const uploadUrl = presigned?.upload_url || presigned?.uploadUrl || presigned?.url;
        if (!uploadUrl) {
          throw new Error('Failed to get upload URL');
        }
        
        // Upload silently - no progress callback, no UI updates
        uploadPromiseRef.current = jobService.uploadToGCS(uploadUrl, file);
        
        await uploadPromiseRef.current;
        setUploadStatus('completed');
        return; // Success, exit the retry loop
      } catch (error) {
        // If this is not the last attempt, wait and retry
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
          continue;
        }
        // Last attempt failed
        setUploadStatus('error');
        // Don't show error to user yet - will be caught on submit
      }
    }
  };

  // Process file selection with duration check
  const processFileSelection = async (file) => {
    if (!file) return;
    
    setSelectedFile(file);
    // Clear example selection when user uploads a file
    setSelectedExample(null);
    setIsUsingExample(false);
    setDurationChecking(true);
    setDurationError(null);
    setFileDuration(null);
    
    // Reset upload state for new file
    setUploadStatus('idle');
    setPresignedData(null);
    uploadPromiseRef.current = null;
    
    // Extract duration
    const duration = await getMediaDuration(file);
    setFileDuration(duration);
    
    // Validate against plan limits
    const maxDuration = usageData?.limits?.max_duration_seconds || 120;
    if (duration && duration > maxDuration) {
      const maxMinutes = Math.floor(maxDuration / 60);
      const maxSeconds = Math.floor(maxDuration % 60);
      const fileMinutes = Math.floor(duration / 60);
      const fileSeconds = Math.floor(duration % 60);
      setDurationError(
        t('Video is {duration} but the maximum allowed duration is {max}. Please trim your video and try again.', {
          duration: `${fileMinutes}:${fileSeconds.toString().padStart(2, '0')}`,
          max: `${maxMinutes}:${maxSeconds.toString().padStart(2, '0')}`,
        })
      );
      setDurationChecking(false);
      return;
    }
    
    setDurationChecking(false);
    
    // Start silent background upload (no UI feedback)
    startSilentUpload(file);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      processFileSelection(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file && (file.type.startsWith('video/') || file.type.startsWith('audio/'))) {
      processFileSelection(file);
    }
  };

  // Handle example video selection
  const handleExampleSelect = (example) => {
    setSelectedExample(example);
    setIsUsingExample(true);
    setSelectedFile(null); // Clear any uploaded file
    setFileDuration(example.duration);
    setDurationError(null);
    setDurationChecking(false);
    // Example videos are already uploaded, so mark upload as completed
    setUploadStatus('completed');
    setPresignedData({ gcs_uri: example.gcsUri });
  };

  const handleCreateProject = async () => {
    // Check if we have either a selected file or an example video
    if ((!selectedFile && !isUsingExample) || !selectedLanguage || !projectName.trim()) return;
    setIsCreatingProject(true);
    setCreateError(null);

    try {
      let gcsUri;
      let fileName;

      // If using example video, use its GCS URI directly
      if (isUsingExample && selectedExample) {
        gcsUri = selectedExample.gcsUri;
        fileName = selectedExample.name;
      } else {
        // Handle regular file upload
        fileName = selectedFile.name;
        
        // Check if background upload is still in progress - wait on button before going to processing
        if (uploadStatus === 'uploading' && uploadPromiseRef.current) {
          try {
            await uploadPromiseRef.current;
            setUploadStatus('completed');
          } catch (error) {
            // Upload failed, will retry below
            setUploadStatus('error');
          }
        }
        
        // If upload completed successfully, use the presigned data
        if (uploadStatus === 'completed' && presignedData) {
          gcsUri = presignedData?.gcs_uri || presignedData?.gcsUri || presignedData?.gcs_path;
        }
        
        // If upload failed or never started, do it now
        if (!gcsUri) {
          const contentType = selectedFile.type || 'application/octet-stream';
          const presigned = await jobService.getPresignedUrl(selectedFile.name, contentType);
          const uploadUrl = presigned?.upload_url || presigned?.uploadUrl || presigned?.url;
          gcsUri = presigned?.gcs_uri || presigned?.gcsUri || presigned?.gcs_path;

          if (!uploadUrl || !gcsUri) {
            throw new Error('Failed to get upload URL.');
          }

          await jobService.uploadToGCS(uploadUrl, selectedFile);
        }
      }

      // Now go to processing screen after upload is complete
      setProcessingProgress(5);
      setProcessingStep(null);
      setProcessingStatus('Submitting transcription...');
      setModalStep(6);
      
      // Build job settings - skip Arabic-specific settings for English/French
      const jobSettings = {
        language: selectedLanguage,
        project_name: projectName.trim(),
        duration_seconds: fileDuration || 0
      };
      
      // Only include Arabic-specific settings for Arabic dialects
      if (!isNonArabicLanguage(selectedLanguage)) {
        jobSettings.script_style = scriptStyle;
        jobSettings.foreign_words_mode = foreignWordsMode;
        jobSettings.chunk_mode = chunkMode;
        jobSettings.words_per_chunk = wordsPerChunk;
        jobSettings.chars_per_chunk = charsPerChunk;
        jobSettings.separate_foreign_chunks = separateForeignChunks;
        jobSettings.split_on_punctuation = splitOnPunctuation;
        jobSettings.split_on_silence = splitOnSilence;
      }
      
      const submission = await jobService.submitJob(gcsUri, fileName, jobSettings);

      const jobId = submission?.job_id || submission?.id || submission?.job?.id;
      if (!jobId) {
        throw new Error('Job created but no ID returned.');
      }

      // Permanently dismiss project creation tour after first successful submission
      dismissProjectTour();

      setCurrentJobId(jobId);
      setProcessingStatus('Processing transcription...');
      setProcessingProgress(5); // Start at 5% after submission

      // Step messages mapping
      const stepMessages = {
        converter: 'Converting media...',
        audiolm: 'Transcribing audio...',
        soniox: 'Refining transcription...',
        insights: 'Finalizing output...',
        completed: 'Complete!'
      };

      // Poll for job status every 2 seconds
      const pollJobStatus = async () => {
        try {
          const job = await jobService.getJob(jobId);
          const status = job?.status;
          const progress = job?.progress;
          const step = job?.current_step;
          const error = job?.error;

          // Update progress (only increase, never decrease)
          if (progress !== undefined && progress !== null) {
            setProcessingProgress(prev => Math.max(prev, progress));
          }

          // Update current step
          if (step) {
            setProcessingStep(step);
            setProcessingStatus(stepMessages[step] || `Processing: ${step}`);
          }

          // Handle completion
          if (status === 'completed') {
            setProcessingProgress(100);
            setProcessingStep('completed');
            setProcessingStatus('Complete!');
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            navigate(`/editor/${jobId}`);
            return;
          }

          // Handle error/failure
          if (status === 'failed') {
            setCreateError(error || 'Processing failed');
            setIsCreatingProject(false);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            return;
          }
        } catch (err) {
          console.error('Failed to poll job status:', err);
          // Don't stop polling on transient errors
        }
      };

      // Start polling immediately and then every 5 seconds
      pollJobStatus();
      pollingIntervalRef.current = setInterval(pollJobStatus, 5000);

    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && typeof detail === 'string' &&
          detail.toLowerCase().includes('verification required')) {
        // The API interceptor opens the phone modal; retry the submission once verified
        retryAfterVerificationRef.current = true;
        setCreateError(null);
        setIsCreatingProject(false);
        setModalStep(5);
        return;
      }
      const videoLimitReached = error?.response?.data?.code === 'VIDEO_LIMIT_REACHED' ||
        error?.response?.status === 429;
      if (videoLimitReached) {
        setCreateError(null);
        setIsCreatingProject(false);
        setIsModalOpen(false);
        setShowLimitReached(true);
        userService.getUsage().then((data) => {
          if (!data) return;
          setUsageData(data);
          setUserPlan(data.plan || 'trial');
          try {
            localStorage.setItem('arabcap_usage_data', JSON.stringify(data));
            localStorage.setItem('arabcap_user_plan', data.plan || 'trial');
          } catch { /* ignore cache failures */ }
        }).catch(() => {});
        return;
      }
      setCreateError(error?.message || 'Failed to create project.');
      setIsCreatingProject(false);
      setModalStep(5);
    }
  };

  const handlePhoneVerified = () => {
    setShowVerificationModal(false);
    if (user) setUserState({ ...user, phone_verified: true });
    if (retryAfterVerificationRef.current) {
      retryAfterVerificationRef.current = false;
      handleCreateProject();
    }
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    setAnimKey((prev) => prev + 1);
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
    setAnimKey((prev) => prev + 1);
  };

  // Language options: geo-aware 7 dialects + fixed Auto Arabic
  const displayedArabicDialects = getDialectSubset(geoCountry);
  const arabicDialectOptions = [...displayedArabicDialects, AUTO_ARABIC];

  const otherLanguages = [
    { id: 'english', name: 'English', flag: '🇬🇧', native: 'English' },
    { id: 'french', name: 'French', flag: '🇫🇷', native: 'Français' },
  ];

  const comingSoonLanguages = [
    { id: 'spanish', name: 'Spanish', flag: '🇪🇸' },
    { id: 'portuguese', name: 'Portuguese', flag: '🇵🇹' },
  ];
  
  // Helper to check if language is non-Arabic (skip script/foreign words steps)
  const isNonArabicLanguage = (lang) => {
    return ['english', 'french', 'arabizi'].includes(lang);
  };

  const getLanguageDisplayInfo = (lang) => {
    if (lang === 'arabizi') {
      return { name: 'Arabizi', flag: '🔤', native: 'Latin script' };
    }
    const dialect = getDialectById(lang);
    if (dialect) return dialect;
    return (
      [...ALL_ARABIC_DIALECTS, AUTO_ARABIC, ...otherLanguages].find((l) => l.id === lang) || {
        name: lang || 'Language',
        flag: '🌐',
      }
    );
  };

  // Pre-select local dialect when opening a new project (or when geo resolves)
  useEffect(() => {
    if (!isModalOpen || userPickedLanguageRef.current) return;
    setSelectedLanguage(getPrimaryDialectId(geoCountry));
  }, [isModalOpen, geoCountry]);

  // Arabizi enforces code-switched foreign words in UI (hidden from user choices)
  useEffect(() => {
    if (selectedLanguage === 'arabizi' && foreignWordsMode !== 'code_switched') {
      setForeignWordsMode('code_switched');
    }
  }, [selectedLanguage, foreignWordsMode]);

  // Track step changes for animation - use a ref to avoid re-renders
  const stepChangeKey = useRef(0);
  const prevStepRef = useRef(1);
  
  // Wrapper to update step with animation direction tracking
  const goToStep = (newStep) => {
    prevStepRef.current = modalStep;
    stepChangeKey.current += 1;
    setModalStep(newStep);
  };
  
  const isGoingForward = modalStep > prevStepRef.current;

  // Modal content rendered directly (not as a function to avoid recreation)
  const modalContent = isModalOpen ? createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 font-sans h-[100dvh]">
        <div
          className={`absolute inset-0 bg-slate-900/60 backdrop-blur-xl transition-opacity duration-300 ${
            isCreatingProject || uploadStatus === 'uploading' ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={() => {
            // Prevent closing modal when uploading or creating project
            if (!isCreatingProject && uploadStatus !== 'uploading') {
              closeNewProjectModal();
            }
          }}
        />
        <div className="relative bg-white rounded-[32px] md:rounded-[40px] w-full max-w-2xl shadow-2xl overflow-hidden animate-[scaleUp_0.3s_cubic-bezier(0.16,1,0.3,1)] border border-white/50 max-h-[90dvh] flex flex-col">
          {/* Progress bar - 5 steps */}
          {modalStep !== 6 && (
            <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100 shrink-0 z-20">
              <div
                className="h-full bg-[#f73140] transition-all duration-500 ease-out"
                style={{ width: `${(modalStep / 5) * 100}%` }}
              />
            </div>
          )}

          {/* Close button */}
          {modalStep !== 6 && (
            <button
              onClick={closeNewProjectModal}
              className={`absolute top-4 end-4 md:top-6 md:end-6 z-20 p-2 md:p-2.5 rounded-full transition-all ${
                isCreatingProject || uploadStatus === 'uploading'
                  ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                  : 'bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-800'
              }`}
              disabled={isCreatingProject || uploadStatus === 'uploading'}
            >
              <XIcon size={18} />
            </button>
          )}

          {createError && modalStep !== 6 && (
            <div className="mx-6 mt-10 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
              {t(createError)}
            </div>
          )}

          {/* Step 6: Processing */}
          {modalStep === 6 && (
            <div className="p-8 md:p-10 flex flex-col items-center justify-center h-full min-h-[450px] animate-[fadeIn_0.5s_ease-out]">
              {/* Circular Progress Indicator */}
              <div className="relative w-36 h-36 mb-8">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  {/* Background circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    stroke="#f1f5f9"
                    strokeWidth="8"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    stroke="#f73140"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - processingProgress / 100)}`}
                    className="transition-all duration-500 ease-out"
                  />
                </svg>
                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black text-slate-900">{processingProgress ?? 0}%</span>
                </div>
              </div>

              <h3 className="text-xl font-extrabold text-slate-900 mb-2 text-center tracking-tight">
                {processingProgress === 100 ? t('Complete!') : t('Processing your video')}
              </h3>
              <p className="text-slate-400 font-semibold text-sm mb-8 text-center">
                {t(processingStatus)}
              </p>

              {/* Step Progress List */}
              <div className="w-full max-w-xs space-y-3 mb-8">
                {[
                  { id: 'converter', label: 'Converting Media', threshold: 25 },
                  { id: 'audiolm', label: 'Transcribing Audio', threshold: 50 },
                  { id: 'soniox', label: 'Refining Output', threshold: 75 },
                  { id: 'insights', label: 'Finalizing', threshold: 95 },
                ].map((step) => {
                  const isCompleted = processingProgress >= step.threshold;
                  const isCurrent = processingStep === step.id;
                  return (
                    <div key={step.id} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all ${
                        isCompleted 
                          ? 'bg-[#f73140] text-white' 
                          : isCurrent 
                            ? 'bg-[#f73140]/10 border-2 border-[#f73140]' 
                            : 'bg-slate-100 border-2 border-slate-200'
                      }`}>
                        {isCompleted ? (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : isCurrent ? (
                          <div className="w-2 h-2 bg-[#f73140] rounded-full animate-pulse" />
                        ) : null}
                      </div>
                      <span className={`text-sm font-semibold ${
                        isCompleted ? 'text-slate-900' : isCurrent ? 'text-[#f73140]' : 'text-slate-400'
                      }`}>
                        {t(step.label)}
                      </span>
                      {isCurrent && (
                        <div className="ms-auto w-4 h-4 border-2 border-slate-200 border-t-[#f73140] rounded-full animate-spin" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Go to Dashboard button */}
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  // Refresh projects to show the new one
                  dashboardService.getJobs(currentPage, itemsPerPage).then((data) => {
                    const { jobs, total } = extractJobsResponse(data);
                    setProjects(jobs.map((job, index) => mapJobToProject(job, index)));
                    setJobsTotal(total);
                  });
                }}
                className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors"
              >
                {t('Go to Dashboard')}
              </button>
              <p className="text-[10px] text-slate-400 mt-2">{t('You can track progress from your dashboard')}</p>
            </div>
          )}

          {/* Step 1: Upload */}
          {modalStep === 1 && (
            <div 
              key={`step1-${stepChangeKey.current}`}
              className="p-6 md:p-10 text-center flex flex-col overflow-y-auto animate-slideContent"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto bg-[#f73140]/5 rounded-2xl flex items-center justify-center text-[#f73140] mb-5 ring-1 ring-[#f73140]/10 shadow-inner shrink-0">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2 tracking-tight">{t('Upload your media')}</h2>
              <p className="text-sm text-slate-400 mb-6">{t('Step {n} of {total}', { n: 1, total: 5 })}</p>

              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`group w-full h-44 md:h-52 border-2 border-dashed rounded-3xl flex flex-col items-center justify-center cursor-pointer transition-all duration-300 relative overflow-hidden ${
                  selectedFile
                    ? 'border-[#f73140] bg-[#f73140]/5'
                    : isDragging
                      ? 'border-[#f73140] bg-[#f73140]/5 scale-[1.02]'
                      : 'border-slate-200 bg-slate-50 hover:bg-white hover:border-[#f73140]/30 hover:shadow-xl'
                }`}
              >
                {selectedFile ? (
                  <div className="flex flex-col items-center">
                    <div className={`w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-3 ring-1 ${durationError ? 'ring-red-200 text-red-500' : 'ring-[#f73140]/20 text-[#f73140]'}`}>
                      {durationChecking ? (
                        <div className="w-5 h-5 border-2 border-slate-200 border-t-[#f73140] rounded-full animate-spin" />
                      ) : durationError ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <p className="text-slate-900 font-bold text-lg px-4 truncate max-w-xs">{selectedFile.name}</p>
                    <p className="text-slate-400 text-xs font-medium mt-1">
                      {t('{size} MB', { size: (selectedFile.size / (1024 * 1024)).toFixed(2) })}
                      {fileDuration && !durationError && ` · ${formatDurationSeconds(fileDuration)}`}
                    </p>
                    <span className="mt-4 px-4 py-1.5 bg-white rounded-full text-xs font-bold text-slate-400 hover:text-[#f73140] transition-colors shadow-sm border border-slate-100">
                      {t('Change file')}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform ring-1 ring-slate-100 text-slate-400">
                      <PlusIcon />
                    </div>
                    <p className="text-slate-900 font-bold text-lg group-hover:text-[#f73140] transition-colors">{t('Drag and drop or Browse')}</p>
                    <p className="text-xs text-slate-400 mt-1">{t('MP4, MOV, MP3 · Max 500MB')}</p>
                  </>
                )}
                <input type="file" ref={fileInputRef} className="hidden" accept="video/*,audio/*" onChange={handleFileSelect} />
              </div>

              {/* Duration error message */}
              {durationError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm font-semibold text-red-600 text-center">{durationError}</p>
                </div>
              )}

              {/* Divider */}
              <div className="relative my-4 md:my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-slate-400 font-semibold">{t('Or try an example')}</span>
                </div>
              </div>

              {/* Example Audio Files - Compact */}
              <div className="space-y-1.5 md:space-y-2 mb-4 md:mb-6">
                {EXAMPLE_AUDIOS.map((example) => (
                  <div
                    key={example.id}
                    onClick={() => {
                      if (previewAudio?.id === example.id) {
                        togglePlayPause();
                      } else {
                        playAudio(example);
                      }
                    }}
                    className={`flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      isUsingExample && selectedExample?.id === example.id
                        ? 'border-[#f73140] bg-[#f73140]/5'
                        : previewAudio?.id === example.id && isPlaying
                          ? 'border-[#f73140] bg-[#f73140]/5'
                          : 'border-slate-200 hover:border-[#f73140]/30 hover:bg-slate-50'
                    }`}
                  >
                    {/* Audio Icon with Play/Pause State */}
                    <div className={`relative w-9 h-9 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-[#f73140]/10 to-[#ff6b6b]/10 flex items-center justify-center flex-shrink-0 transition-all ${
                      previewAudio?.id === example.id && isPlaying ? 'scale-110' : ''
                    }`}>
                      {previewAudio?.id === example.id && isPlaying ? (
                        <>
                          {/* Animated sound waves */}
                          <div className="absolute inset-0 flex items-center justify-center gap-0.5">
                            <div className="w-0.5 bg-[#f73140] rounded-full animate-[wave1_0.6s_ease-in-out_infinite]" style={{ height: '40%' }} />
                            <div className="w-0.5 bg-[#f73140] rounded-full animate-[wave2_0.6s_ease-in-out_infinite_0.1s]" style={{ height: '60%' }} />
                            <div className="w-0.5 bg-[#f73140] rounded-full animate-[wave3_0.6s_ease-in-out_infinite_0.2s]" style={{ height: '50%' }} />
                            <div className="w-0.5 bg-[#f73140] rounded-full animate-[wave2_0.6s_ease-in-out_infinite_0.3s]" style={{ height: '70%' }} />
                            <div className="w-0.5 bg-[#f73140] rounded-full animate-[wave1_0.6s_ease-in-out_infinite_0.4s]" style={{ height: '45%' }} />
                          </div>
                          <style>{`
                            @keyframes wave1 {
                              0%, 100% { height: 30%; }
                              50% { height: 60%; }
                            }
                            @keyframes wave2 {
                              0%, 100% { height: 50%; }
                              50% { height: 80%; }
                            }
                            @keyframes wave3 {
                              0%, 100% { height: 40%; }
                              50% { height: 70%; }
                            }
                          `}</style>
                        </>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2">
                          <path d="M9 18V5l12-2v13" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 text-start">
                      <p className={`text-xs md:text-sm font-bold truncate text-start ${
                        isUsingExample && selectedExample?.id === example.id ? 'text-[#f73140]' : 'text-slate-900'
                      }`}>
                        {example.name}
                      </p>
                      <div className="flex items-center gap-2 text-[11px] md:text-xs text-slate-400 justify-start">
                        <span className="truncate">{t(example.dialect)}</span>
                        <span className="hidden sm:inline">·</span>
                        <span className="hidden sm:inline">{formatDurationSeconds(example.duration)}</span>
                      </div>
                    </div>

                    {/* Use Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExampleSelect(example);
                        if (previewAudio?.id === example.id) {
                          closeAudioPlayer();
                        }
                      }}
                      className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm font-bold transition-all ${
                        isUsingExample && selectedExample?.id === example.id
                          ? 'bg-[#f73140] text-white shadow-lg shadow-[#f73140]/30'
                          : 'bg-slate-900 text-white shadow-md shadow-slate-900/20 hover:bg-slate-800 hover:shadow-xl hover:shadow-slate-900/30 hover:scale-105 active:scale-95'
                      }`}
                    >
                      {isUsingExample && selectedExample?.id === example.id ? (
                        <span className="flex items-center gap-1.5">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          {t('Selected')}
                        </span>
                      ) : (
                        t('Use This')
                      )}
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => ((selectedFile || isUsingExample) && !durationChecking && !durationError) && goToStep(2)}
                  disabled={(!selectedFile && !isUsingExample) || durationChecking || durationError}
                  className={`w-full md:w-auto px-10 py-3.5 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    (selectedFile || isUsingExample) && !durationChecking && !durationError 
                      ? 'bg-[#f73140] text-white shadow-lg shadow-[#f73140]/20 hover:bg-[#d62331]' 
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {durationChecking ? (
                    <>
                      <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
                      {t('Checking...')}
                    </>
                  ) : (
                    <>{t('Continue')} <ArrowRightIcon /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Language Selection */}
          {modalStep === 2 && (
            <div 
              key={`step2-${stepChangeKey.current}`}
              className={`p-6 md:p-10 flex flex-col overflow-y-auto ${isGoingForward !== isRTL ? 'animate-slideInRight' : 'animate-slideInLeft'}`}
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto bg-[#f73140]/5 rounded-2xl flex items-center justify-center text-[#f73140] mb-4 ring-1 ring-[#f73140]/10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2">{t('Choose your language')}</h2>
                <p className="text-sm text-slate-400">{t('Step {n} of {total}', { n: 2, total: 5 })}</p>
              </div>

              {/* Arabic Dialects — 7 geo-ranked + Auto Arabic */}
              <div className="grid grid-cols-4 gap-2">
                {arabicDialectOptions.map((lang) => (
                <button
                    key={lang.id}
                    onClick={() => {
                      userPickedLanguageRef.current = true;
                      setSelectedLanguage(lang.id);
                    }}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      selectedLanguage === lang.id
                        ? 'border-[#f73140] bg-[#f73140]/5 scale-105'
                      : 'border-slate-100 bg-white hover:border-[#f73140]/30 hover:bg-slate-50'
                  }`}
                >
                    <span className="text-2xl block mb-1">{lang.flag}</span>
                    <span className={`text-[11px] font-bold block ${selectedLanguage === lang.id ? 'text-[#f73140]' : 'text-slate-600'}`}>
                      {t(lang.name)}
                    </span>
                  </button>
                ))}
              </div>

              {/* Other Languages */}
              <div className="pt-4 border-t border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 text-center">{t('Other Languages')}</p>
                <div className="grid grid-cols-4 gap-2">
                  {/* English & French - selectable */}
                  {otherLanguages.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => {
                        userPickedLanguageRef.current = true;
                        setSelectedLanguage(lang.id);
                      }}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        selectedLanguage === lang.id
                          ? 'border-[#f73140] bg-[#f73140]/5 scale-105'
                          : 'border-slate-100 bg-white hover:border-[#f73140]/30 hover:bg-slate-50'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{lang.flag}</span>
                      <span className={`text-[11px] font-bold block ${selectedLanguage === lang.id ? 'text-[#f73140]' : 'text-slate-600'}`}>
                        {t(lang.name)}
                      </span>
                    </button>
                  ))}
                  {/* Spanish & Portuguese - coming soon (greyed out) */}
                  {comingSoonLanguages.map((lang) => (
                    <div key={lang.id} className="p-3 rounded-xl border-2 border-slate-100 bg-slate-50/50 text-center opacity-40 cursor-not-allowed relative">
                      <span className="text-xl block mb-1 grayscale">{lang.flag}</span>
                      <span className="text-[10px] font-bold block text-slate-400">{t(lang.name)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation */}
              <div className="mt-8 flex items-center gap-3">
                <button onClick={() => goToStep(1)} className="py-3 px-6 rounded-xl text-sm font-bold text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
                  {t('Back')}
                </button>
                <button
                  onClick={() => {
                    if (selectedLanguage) {
                      // Skip to step 5 (project name) for English/French
                      goToStep(isNonArabicLanguage(selectedLanguage) ? 5 : 3);
                    }
                  }}
                  disabled={!selectedLanguage}
                  className={`flex-1 py-3.5 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    selectedLanguage ? 'bg-[#f73140] text-white hover:bg-[#d62331]' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {t('Continue')} <ArrowRightIcon />
                </button>
                  </div>
            </div>
          )}

          {/* Step 3: Script Style */}
          {modalStep === 3 && (
            <div 
              key={`step3-${stepChangeKey.current}`}
              className={`p-6 md:p-10 flex flex-col overflow-y-auto ${isGoingForward !== isRTL ? 'animate-slideInRight' : 'animate-slideInLeft'}`}
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto bg-[#f73140]/5 rounded-2xl flex items-center justify-center text-[#f73140] mb-4 ring-1 ring-[#f73140]/10">
                  <span className="text-2xl font-bold font-serif">ض</span>
                </div>
                <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2">{t('Script style')}</h2>
                <p className="text-sm text-slate-400">{t('Step {n} of {total}', { n: 3, total: 5 })}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Standard Arabic */}
                <button
                  onClick={() => {
                    setScriptStyle('standard');
                    // Arabizi overwrites selectedLanguage — restore the step-2 dialect
                    if (selectedLanguage === 'arabizi') {
                      setSelectedLanguage(
                        dialectBeforeArabiziRef.current || getPrimaryDialectId(geoCountry)
                      );
                    }
                  }}
                  className={`p-5 rounded-2xl border-2 transition-all text-start ${
                    scriptStyle === 'standard'
                      ? 'border-[#f73140] bg-[#f73140]/5'
                      : 'border-slate-100 bg-white hover:border-[#f73140]/30'
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#fff0f1] text-[#f73140] flex items-center justify-center text-2xl font-bold font-serif mb-3">ض</div>
                  <h3 className={`font-bold text-lg mb-1 ${scriptStyle === 'standard' ? 'text-[#f73140]' : 'text-slate-900'}`}>{t('Standard Arabic')}</h3>
                  <p className="text-xs text-slate-500 mb-3">{t('Arabic script transcription')}</p>
                  <div className="bg-slate-50 rounded-lg p-3 text-right">
                    <span className="text-sm text-slate-700 font-medium" dir="rtl">كيفاش نقدر نساعدك؟</span>
                  </div>
                </button>

                {/* Arabizi */}
                <button
                  onClick={() => {
                    if (selectedLanguage && selectedLanguage !== 'arabizi') {
                      dialectBeforeArabiziRef.current = selectedLanguage;
                    }
                    setScriptStyle('arabizi');
                    userPickedLanguageRef.current = true;
                    setSelectedLanguage('arabizi');
                  }}
                  className={`p-5 rounded-2xl border-2 transition-all text-start ${
                    scriptStyle === 'arabizi'
                      ? 'border-[#f73140] bg-[#f73140]/5'
                      : 'border-slate-100 bg-white hover:border-[#f73140]/30'
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#e8f5ff] text-[#2196f3] flex items-center justify-center text-2xl font-black mb-3">A</div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-bold text-lg ${scriptStyle === 'arabizi' ? 'text-[#f73140]' : 'text-slate-900'}`}>{t('Arabizi')}</h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200">
                      {t('Beta')}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">{t('Latin script transcription')}</p>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <span className="text-sm text-slate-700 font-medium" dir="ltr">kifach n9der nsa3dek?</span>
                  </div>
                </button>
              </div>

              {/* Navigation */}
              <div className="mt-auto flex items-center gap-3">
                <button onClick={() => goToStep(2)} className="py-3 px-6 rounded-xl text-sm font-bold text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
                  {t('Back')}
                </button>
                <button
                  onClick={() => goToStep(scriptStyle === 'arabizi' ? 5 : 4)}
                  className="flex-1 py-3.5 rounded-2xl text-sm font-bold bg-[#f73140] text-white hover:bg-[#d62331] transition-all flex items-center justify-center gap-2"
                >
                  {t('Continue')} <ArrowRightIcon />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Foreign Words */}
          {modalStep === 4 && scriptStyle !== 'arabizi' && selectedLanguage !== 'arabizi' && (
            <div 
              key={`step4-${stepChangeKey.current}`}
              className={`p-6 md:p-10 flex flex-col overflow-y-auto ${isGoingForward !== isRTL ? 'animate-slideInRight' : 'animate-slideInLeft'}`}
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto bg-[#f73140]/5 rounded-2xl flex items-center justify-center text-[#f73140] mb-4 ring-1 ring-[#f73140]/10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 8l6 6" />
                    <path d="M4 14l6-6 2-3" />
                    <path d="M2 5h12" />
                    <path d="M7 2h1" />
                    <path d="M22 22l-5-10-5 10" />
                    <path d="M14 18h6" />
                  </svg>
                </div>
                <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2">{t('Foreign words')}</h2>
                <p className="text-sm text-slate-400">{t('Step {n} of {total}', { n: 4, total: 5 })} · {t('How to handle words from other languages?')}</p>
              </div>

              <div className="space-y-3 mb-6">
                <>
                {/* Transliterated (Default) - Arabic script for everything */}
                <button
                  onClick={() => setForeignWordsMode('transliterated')}
                  className={`w-full p-4 rounded-2xl border-2 transition-all text-start ${
                    foreignWordsMode === 'transliterated'
                      ? 'border-[#f73140] bg-[#f73140]/5'
                      : 'border-slate-100 bg-white hover:border-[#f73140]/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className={`font-bold ${foreignWordsMode === 'transliterated' ? 'text-[#f73140]' : 'text-slate-900'}`}>
                        {t('Transliterated')}
                      </h3>
                      <p className="text-xs text-slate-400">{t('Write foreign words in Arabic letters (Default)')}</p>
                  </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      foreignWordsMode === 'transliterated' ? 'border-[#f73140] bg-[#f73140]' : 'border-slate-300'
                    }`}>
                      {foreignWordsMode === 'transliterated' && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="bg-slate-100 px-2.5 py-1 rounded-lg text-slate-500 text-xs" dir="rtl">عندي meeting</span>
                    <span className="text-slate-400 inline-block rtl:-scale-x-100">→</span>
                    <span className="bg-[#f73140]/10 px-2.5 py-1 rounded-lg text-[#f73140] font-medium text-xs" dir="rtl">عندي ميتينغ</span>
                  </div>
                </button>

                {/* Code-switched - Keep original Latin script */}
                <button
                  onClick={() => setForeignWordsMode('code_switched')}
                  className={`w-full p-4 rounded-2xl border-2 transition-all text-start ${
                    foreignWordsMode === 'code_switched'
                      ? 'border-[#f73140] bg-[#f73140]/5'
                      : 'border-slate-100 bg-white hover:border-[#f73140]/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className={`font-bold ${foreignWordsMode === 'code_switched' ? 'text-[#f73140]' : 'text-slate-900'}`}>
                        {t('Code-switched')}
                      </h3>
                      <p className="text-xs text-slate-400">{t('Keep foreign words in original script')}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      foreignWordsMode === 'code_switched' ? 'border-[#f73140] bg-[#f73140]' : 'border-slate-300'
                    }`}>
                      {foreignWordsMode === 'code_switched' && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="bg-slate-100 px-2.5 py-1 rounded-lg text-slate-500 text-xs" dir="rtl">عندي meeting</span>
                    <span className="text-slate-400 inline-block rtl:-scale-x-100">→</span>
                    <span className="bg-[#f73140]/10 px-2.5 py-1 rounded-lg text-[#f73140] font-medium text-xs" dir="rtl">عندي meeting</span>
                  </div>
                </button>

                {/* Translated - Translate to Arabic */}
                <button
                  onClick={() => setForeignWordsMode('translated')}
                  className={`w-full p-4 rounded-2xl border-2 transition-all text-start ${
                    foreignWordsMode === 'translated'
                      ? 'border-[#f73140] bg-[#f73140]/5'
                      : 'border-slate-100 bg-white hover:border-[#f73140]/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className={`font-bold ${foreignWordsMode === 'translated' ? 'text-[#f73140]' : 'text-slate-900'}`}>
                        {t('Translated')}
                      </h3>
                      <p className="text-xs text-slate-400">{t('Translate foreign words to Arabic')}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      foreignWordsMode === 'translated' ? 'border-[#f73140] bg-[#f73140]' : 'border-slate-300'
                    }`}>
                      {foreignWordsMode === 'translated' && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="bg-slate-100 px-2.5 py-1 rounded-lg text-slate-500 text-xs" dir="rtl">عندي meeting</span>
                    <span className="text-slate-400 inline-block rtl:-scale-x-100">→</span>
                    <span className="bg-[#f73140]/10 px-2.5 py-1 rounded-lg text-[#f73140] font-medium text-xs" dir="rtl">عندي اجتماع</span>
                  </div>
                </button>

                <p className="text-[10px] text-slate-400 text-center pt-2">{t('You can change this later in the editor')}</p>
                </>
              </div>

              {/* Navigation */}
              <div className="mt-auto flex items-center gap-3">
                <button onClick={() => goToStep(3)} className="py-3 px-6 rounded-xl text-sm font-bold text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
                  {t('Back')}
                </button>
                <button
                  onClick={() => goToStep(5)}
                  disabled={!foreignWordsMode}
                  className={`flex-1 py-3.5 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    foreignWordsMode 
                      ? 'bg-[#f73140] text-white hover:bg-[#d62331]' 
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {t('Continue')} <ArrowRightIcon />
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Project Details */}
          {modalStep === 5 && (
            <div 
              key={`step5-${stepChangeKey.current}`}
              className={`p-6 md:p-10 flex flex-col overflow-y-auto ${isGoingForward !== isRTL ? 'animate-slideInRight' : 'animate-slideInLeft'}`}
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto bg-[#f73140]/5 rounded-2xl flex items-center justify-center text-[#f73140] mb-4 ring-1 ring-[#f73140]/10">
                  <SparkleIcon />
                </div>
                <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2">{t('Almost there!')}</h2>
                <p className="text-sm text-slate-400">{t('Step {n} of {total}', { n: 5, total: 5 })}</p>
              </div>

              {/* Project Name */}
              <div className="mb-5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">{t('Project Name')}</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder={t('Enter project name...')}
                  className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-100 bg-white text-slate-900 font-medium focus:border-[#f73140] focus:ring-2 focus:ring-[#f73140]/20 outline-none transition-all text-lg"
                  autoFocus
                />
              </div>

              {/* Advanced Settings */}
              <div className="mb-5">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center justify-between w-full p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-bold text-slate-500">{t('Advanced Settings')}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-slate-400 transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {showAdvanced && (
                  <div className="mt-2 p-4 rounded-xl border border-slate-100 bg-white space-y-4" style={{ animation: 'slideDown 0.2s ease-out' }}>
                    {/* Chunk Mode Selector */}
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">{t('Chunk by')}</label>
                      <div className="grid grid-cols-2 gap-2 p-1 rounded-xl border border-slate-200 bg-slate-50">
                        <button
                          onClick={() => setChunkMode('words')}
                          className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
                            chunkMode === 'words' ? 'bg-[#f73140] text-white shadow-md' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {t('Words')}
                        </button>
                        <button
                          onClick={() => setChunkMode('characters')}
                          className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
                            chunkMode === 'characters' ? 'bg-[#f73140] text-white shadow-md' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {t('Characters')}
                        </button>
                      </div>
                    </div>

                    {/* Words per Chunk */}
                    {chunkMode === 'words' && (
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-bold text-slate-600">{t('Words per chunk')}</span>
                          <span className="font-bold text-[#f73140]">{wordsPerChunk}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="12"
                          value={wordsPerChunk}
                          onChange={(e) => setWordsPerChunk(parseInt(e.target.value))}
                          className="w-full h-2 rounded-full appearance-none cursor-pointer accent-[#f73140] bg-slate-200"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">
                          {t(wordsPerChunk <= 4 ? 'Short bursts' : wordsPerChunk <= 8 ? 'Balanced' : 'Long phrases')}
                        </p>
                      </div>
                    )}

                    {/* Characters per Chunk */}
                    {chunkMode === 'characters' && (
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-bold text-slate-600">{t('Characters per chunk')}</span>
                          <span className="font-bold text-[#f73140]">{charsPerChunk}</span>
                        </div>
                        <input
                          type="range"
                          min="10"
                          max="150"
                          step="5"
                          value={charsPerChunk}
                          onChange={(e) => setCharsPerChunk(parseInt(e.target.value))}
                          className="w-full h-2 rounded-full appearance-none cursor-pointer accent-[#f73140] bg-slate-200"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">
                          {t(charsPerChunk <= 30 ? 'Compact' : charsPerChunk <= 80 ? 'Balanced' : 'Extended')}
                        </p>
                      </div>
                    )}

                    {/* Separate Foreign Words - only if keep_latin */}
                    {foreignWordsMode === 'keep_latin' && (
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <div>
                          <div className="text-sm font-bold text-slate-600">{t('Separate foreign words')}</div>
                          <div className="text-[10px] text-slate-400">{t('Keep Latin phrases in separate chunks')}</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={separateForeignChunks}
                            onChange={(e) => setSeparateForeignChunks(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#f73140]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#f73140] bg-slate-200"></div>
                        </label>
                      </div>
                    )}

                    {/* Split on Punctuation */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div>
                        <div className="text-sm font-bold text-slate-600">{t('Split on punctuation')}</div>
                        <div className="text-[10px] text-slate-400">{t('Break phrases at periods, commas, etc.')}</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={splitOnPunctuation}
                          onChange={(e) => setSplitOnPunctuation(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#f73140]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#f73140] bg-slate-200"></div>
                      </label>
                    </div>

                    {/* Split on Silence */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div>
                        <div className="text-sm font-bold text-slate-600">{t('Split on silence')}</div>
                        <div className="text-[10px] text-slate-400">{t('Break phrases at natural pauses in speech')}</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={splitOnSilence}
                          onChange={(e) => setSplitOnSilence(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#f73140]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#f73140] bg-slate-200"></div>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 mb-6">
                {selectedLanguage === 'arabizi' ? (
                  <div className="grid grid-cols-2 gap-4 text-center text-sm">
                    <div>
                      <span className="text-2xl block mb-1">{getLanguageDisplayInfo(selectedLanguage).flag}</span>
                      <span className="text-slate-500">{t(getLanguageDisplayInfo(selectedLanguage).name)}</span>
                    </div>
                    <div>
                      <span className="text-2xl block mb-1 font-black text-[#2196f3]">A</span>
                      <span className="text-slate-500">{t('Latin Script')}</span>
                    </div>
                  </div>
                ) : isNonArabicLanguage(selectedLanguage) ? (
                  // Simple summary for English/French
                  <div className="text-center text-sm">
                    <span className="text-3xl block mb-2">{getLanguageDisplayInfo(selectedLanguage).flag}</span>
                    <span className="text-slate-700 font-medium">{t('{language} Transcription', { language: t(getLanguageDisplayInfo(selectedLanguage).name) })}</span>
                  </div>
                ) : (
                  // Full summary for Arabic
                  <div className="grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <span className="text-2xl block mb-1">{getLanguageDisplayInfo(selectedLanguage).flag}</span>
                      <span className="text-slate-500">{t(getLanguageDisplayInfo(selectedLanguage).name)}</span>
                    </div>
                    <div>
                      <span className="text-2xl block mb-1 font-serif font-bold text-[#f73140]">ض</span>
                      <span className="text-slate-500">{t('Arabic Script')}</span>
                    </div>
                    <div>
                      <span className="text-2xl block mb-1">
                        {foreignWordsMode === 'transliterated' ? '✍️' : foreignWordsMode === 'translated' ? '🔄' : '🌍'}
                      </span>
                      <span className="text-slate-500">
                        {t(foreignWordsMode === 'transliterated' ? 'Arabic Script' : foreignWordsMode === 'translated' ? 'Translate' : 'Keep Latin')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="mt-auto flex items-center gap-3">
                <button
                  onClick={() => {
                    if (scriptStyle === 'arabizi' || selectedLanguage === 'arabizi') {
                      goToStep(3);
                    } else if (isNonArabicLanguage(selectedLanguage)) {
                      goToStep(2);
                    } else {
                      goToStep(4);
                    }
                  }}
                  className="py-3 px-6 rounded-xl text-sm font-bold text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors"
                >
                  {t('Back')}
                </button>
                <button
                  onClick={handleCreateProject}
                  disabled={!projectName.trim() || isCreatingProject}
                  className={`flex-1 py-3.5 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    isCreatingProject 
                      ? 'bg-[#f73140] text-white cursor-wait opacity-90' 
                      : projectName.trim() 
                        ? 'bg-[#f73140] text-white hover:bg-[#d62331] shadow-lg shadow-[#f73140]/20' 
                        : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {isCreatingProject ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t(uploadStatus === 'uploading' ? 'Finishing upload...' : 'Starting...')}
                    </>
                  ) : (
                    <>
                      <SparkleIcon />
                      {t('Start Transcription')}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes slideIn {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes slideInLeft {
            from { opacity: 0; transform: translateX(-30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes slideContent {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideDown {
            from { opacity: 0; max-height: 0; }
            to { opacity: 1; max-height: 200px; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.98); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes scaleUp {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes progress {
            0% { width: 0%; }
            100% { width: 100%; }
          }
          .animate-slideInRight {
            animation: slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .animate-slideInLeft {
            animation: slideInLeft 0.35s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .animate-slideContent {
            animation: slideContent 0.35s cubic-bezier(0.16, 1, 0.3, 1);
          }
        `}</style>
      </div>,
      document.body
    ) : null;

  // Manifesto Modal - Multi-step Story
  const manifestoModal = showManifesto ? createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 h-[100dvh]" style={isRTL ? undefined : { fontFamily: "'SF Pro Display', system-ui, -apple-system, sans-serif" }}>
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl transition-opacity duration-300"
        onClick={() => setShowManifesto(false)}
      />
      <div className="relative bg-gradient-to-b from-white via-white to-slate-50 rounded-[36px] w-full max-w-xl shadow-2xl overflow-hidden animate-[scaleUp_0.4s_cubic-bezier(0.16,1,0.3,1)] h-[85dvh] max-h-[700px] flex flex-col">
        
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-100 z-20">
          <div 
            className="h-full bg-gradient-to-r from-[#f73140] to-[#ff6b6b] transition-all duration-500 ease-out"
            style={{ width: `${(manifestoStep / totalManifestoSteps) * 100}%` }}
          />
        </div>

        {/* Close button */}
        <button
          onClick={() => setShowManifesto(false)}
          className="absolute top-6 end-6 z-20 p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-600 rounded-full transition-all"
        >
          <XIcon size={18} />
        </button>

        {/* Step indicator */}
        <div className="absolute top-7 start-8 z-20">
          <span className="text-[11px] font-semibold text-slate-300 tracking-widest">{manifestoStep} / {totalManifestoSteps}</span>
        </div>

        {/* Content Container */}
        <div className="flex-1 overflow-hidden flex flex-col">
          
          {/* Step 1: The Hook */}
          {manifestoStep === 1 && (
            <div className="h-full flex flex-col items-center justify-center px-10 md:px-14 py-10 text-center animate-slideContent">
              <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center text-white mb-8 overflow-visible">
                <img
                  src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
                  alt="ArabCap"
                  className="h-12 w-auto scale-[2]"
                />
              </div>
              
              <p className="text-[#f73140] text-[11px] font-bold uppercase tracking-[0.25em] mb-5">{t('Our Story')}</p>
              
              <h1 className="text-[38px] md:text-[44px] leading-[1.05] font-extrabold text-slate-900 mb-5 tracking-tight">
                <span className="text-slate-300">{t('400 million')}</span><br />
                {t('voices.')}
              </h1>
              
              <p className="text-lg md:text-xl text-slate-500 leading-relaxed max-w-sm mb-3">
                {t('Arabic is one of the most spoken languages on Earth.')}
              </p>
              
              <p className="text-lg md:text-xl text-slate-800 font-semibold leading-relaxed max-w-sm">
                {t('Yet AI barely understands it.')}
              </p>

              <div className="flex items-center gap-4 mt-10">
                <div className="w-10 h-px bg-slate-200" />
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.2em]">
                  {t('Small team. Big dream.')}
                </p>
                <div className="w-10 h-px bg-slate-200" />
              </div>
            </div>
          )}

          {/* Step 2: The Problem */}
          {manifestoStep === 2 && (
            <div className="h-full flex flex-col items-center justify-center px-10 md:px-14 py-10 text-center animate-slideContent">
              <p className="text-[#f73140] text-[11px] font-bold uppercase tracking-[0.25em] mb-6">{t('The Challenge')}</p>
              
              <h2 className="text-[32px] md:text-[38px] leading-[1.1] font-extrabold text-slate-900 mb-8 tracking-tight">
                {rich(t("Arabic isn't <em>one</em> language."), { em: (c) => <em className="not-italic text-slate-400">{c}</em> })}<br />
                <span className="text-[#f73140]">{t("It's dozens.")}</span>
              </h2>
              
              <div className="space-y-4 w-full max-w-sm">
                <div className="flex items-start gap-4 text-start bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-500">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 mb-0.5">{t('Real dialects')}</p>
                    <p className="text-sm text-slate-500">{t('Darija. Masri. Khaleeji. Not textbook Arabic.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 text-start bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-500">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 mb-0.5">{t('Languages blend')}</p>
                    <p className="text-sm text-slate-500">"عندي <span className="font-semibold">meeting</span> غدا" — {t('Arabic, French, English. One breath.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 text-start bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-500">
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                      <path d="M9 9h6v6H9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 mb-0.5">{t('Generic AI fails')}</p>
                    <p className="text-sm text-slate-500">{t('Tools trained on English miss everything that makes us, us.')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: The Solution */}
          {manifestoStep === 3 && (
            <div className="h-full flex flex-col items-center justify-center px-10 md:px-14 py-10 text-center animate-slideContent">
              <p className="text-[#f73140] text-[11px] font-bold uppercase tracking-[0.25em] mb-6">{t('Our Approach')}</p>
              
              <h2 className="text-[32px] md:text-[38px] leading-[1.1] font-extrabold text-slate-900 mb-2 tracking-tight">
                {rich(t('Built <em>different</em>.'), { em: (c) => <em className="not-italic font-light text-slate-400">{c}</em> })}
              </h2>
              <p className="text-slate-400 text-sm mb-8">{t('For a reason.')}</p>
              
              <div className="grid gap-3 w-full max-w-sm">
                <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-start">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="w-8 h-8 rounded-lg bg-[#f73140]/10 flex items-center justify-center">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                    </div>
                    <p className="font-bold text-slate-900">{t('Native dialects')}</p>
                  </div>
                  <p className="text-sm text-slate-500 ps-11">{t('How you actually speak. Natural. Authentic.')}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-start">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="w-8 h-8 rounded-lg bg-[#f73140]/10 flex items-center justify-center">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5">
                        <path d="M16 3h5v5" />
                        <path d="M8 3H3v5" />
                        <path d="M21 3l-7 7" />
                        <path d="M3 3l7 7" />
                        <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5" />
                      </svg>
                    </div>
                    <p className="font-bold text-slate-900">{t('Language-aware')}</p>
                  </div>
                  <p className="text-sm text-slate-500 ps-11">{t('Arabic, French, English — mixed together, understood.')}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-start">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="w-8 h-8 rounded-lg bg-[#f73140]/10 flex items-center justify-center">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                      </svg>
                    </div>
                    <p className="font-bold text-slate-900">{t('Fast by design')}</p>
                  </div>
                  <p className="text-sm text-slate-500 ps-11">{t('Edit in seconds. Ship faster. Create more.')}</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: The Impact */}
          {manifestoStep === 4 && (
            <div className="h-full flex flex-col items-center justify-center px-10 md:px-14 py-10 text-center animate-slideContent">
              <p className="text-[#f73140] text-[11px] font-bold uppercase tracking-[0.25em] mb-6">{t('The Why')}</p>
              
              <h2 className="text-[32px] md:text-[38px] leading-[1.1] font-extrabold text-slate-900 mb-2 tracking-tight">
                {t('Beyond subtitles.')}
              </h2>
              <p className="text-slate-400 text-sm mb-8 max-w-xs">{t('Connection. Access. Opportunity.')}</p>
              
              <div className="space-y-4 w-full max-w-sm">
                <div className="flex items-start gap-4 text-start">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0 shadow-lg shadow-purple-500/20">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-base mb-0.5">{t('Accessibility')}</p>
                    <p className="text-sm text-slate-500 leading-relaxed">{t('Opening Arabic content to the deaf and hard of hearing. Everyone deserves access.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 text-start">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shrink-0 shadow-lg shadow-orange-500/20">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-base mb-0.5">{t('Literacy')}</p>
                    <p className="text-sm text-slate-500 leading-relaxed">{t('Helping children connect spoken words to written text. Learning by watching.')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 text-start">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/20">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-base mb-0.5">{t('Global reach')}</p>
                    <p className="text-sm text-slate-500 leading-relaxed">{t('Empowering creators to share their voice with the world.')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Join Us */}
          {manifestoStep === 5 && (
            <div className="h-full flex flex-col items-center justify-center px-10 md:px-14 py-10 text-center animate-slideContent">
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-6 border border-emerald-100">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                {t('Early Access')}
              </div>
              
              <h2 className="text-[32px] md:text-[38px] leading-[1.1] font-extrabold text-slate-900 mb-2 tracking-tight">
                {t('Join the journey.')}
              </h2>
              <p className="text-slate-500 mb-8 max-w-xs text-sm leading-relaxed">
                {t("We're building in public. Learning fast. Shipping faster. And we need you.")}
              </p>
              
              <div className="bg-slate-50 rounded-2xl p-5 text-start max-w-xs w-full mb-6 border border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-3">{t("Shape what's next")}</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#f73140]" />
                    <span className="text-sm text-slate-600">{t('Report bugs you find')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#f73140]" />
                    <span className="text-sm text-slate-600">{t('Share your ideas')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#f73140]" />
                    <span className="text-sm text-slate-600">{t('Tell a creator friend')}</span>
                  </div>
                </div>
              </div>
              
              <div className="w-full max-w-xs space-y-2.5">
                <a
                  href="mailto:hello@arabcap.ai"
                  className="flex items-center justify-center gap-2.5 w-full py-3.5 bg-slate-900 text-white font-bold text-sm rounded-2xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  {t('Say hello')}
                </a>
                <div className="flex gap-2">
                  <a href="https://twitter.com/arabcap" target="_blank" rel="noopener noreferrer" className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-600 font-semibold text-xs rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all text-center">Twitter</a>
                  <a href="https://instagram.com/arabcap" target="_blank" rel="noopener noreferrer" className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-600 font-semibold text-xs rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all text-center">Instagram</a>
                  <a href="https://linkedin.com/company/arabcap" target="_blank" rel="noopener noreferrer" className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-600 font-semibold text-xs rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all text-center">LinkedIn</a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="p-5 flex items-center justify-between border-t border-slate-100 bg-white/80 backdrop-blur-sm">
          <button
            onClick={() => setManifestoStep(Math.max(1, manifestoStep - 1))}
            className={`px-5 py-2.5 text-sm font-semibold rounded-xl transition-all ${
              manifestoStep === 1 
                ? 'text-slate-200 cursor-not-allowed' 
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
            }`}
            disabled={manifestoStep === 1}
          >
            {t('Back')}
          </button>
          
          {manifestoStep < totalManifestoSteps ? (
            <button
              onClick={() => setManifestoStep(manifestoStep + 1)}
              className="px-7 py-3 bg-[#f73140] text-white font-bold text-sm rounded-xl hover:bg-[#d62331] transition-all flex items-center gap-2 shadow-lg shadow-[#f73140]/20"
            >
              {t('Continue')}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="rtl:-scale-x-100">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => setShowManifesto(false)}
              className="px-7 py-3 bg-slate-900 text-white font-bold text-sm rounded-xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
            >
              {t("Let's build")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Limit Reached Modal
  const limitReachedModal = showLimitReached ? createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-[fadeIn_0.3s_ease-out]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#f73140]/10 flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          
          {/* Trial users - Show upgrade message */}
          {(userPlan === 'trial' || userPlan === 'free') && (
            <>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('Trial Videos Used')}</h3>
              <p className="text-slate-500 mb-6">
                {t("You've used all your trial videos. Upgrade to continue creating amazing content!")}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowLimitReached(false);
                    setShowUpgradeModal(true);
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#f73140] to-[#f73140]/90 text-white font-bold rounded-xl hover:shadow-lg transition-all"
                >
                  {t('View Plans')}
                </button>
                <button
                  onClick={() => setShowLimitReached(false)}
                  className="w-full px-6 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                >
                  {t('Maybe Later')}
                </button>
              </div>
            </>
          )}
          
          {/* Starter users - Encourage upgrade to Pro */}
          {userPlan === 'starter' && (
            <>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('Monthly Limit Reached')}</h3>
              <p className="text-slate-500 mb-6">
                {t("You've used all {count} videos for this month. Upgrade to Pro for 30 videos per month!", { count: usageData?.limits?.videos_per_month || 8 })}
              </p>
              <div className="space-y-3">
                <button
                  onClick={async () => {
                    setShowLimitReached(false);
                    await handleManageSubscription();
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#f73140] to-[#f73140]/90 text-white font-bold rounded-xl hover:shadow-lg transition-all"
                >
                  {t('Upgrade to Pro')}
                </button>
                <button
                  onClick={() => setShowLimitReached(false)}
                  className="w-full px-6 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                >
                  {t('Got It')}
                </button>
              </div>
            </>
          )}
          
          {/* Pro users - Contact us or upgrade to Max */}
          {userPlan === 'pro' && (
            <>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('Monthly Limit Reached')}</h3>
              <p className="text-slate-500 mb-6">
                {t("You've used all {count} videos for this month. Need more? Upgrade to Max or contact us!", { count: usageData?.limits?.videos_per_month || 30 })}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => { setShowLimitReached(false); handleUpgradeToPlan('max'); }}
                  className="w-full px-6 py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-all flex items-center justify-center gap-3"
                >
                  <span>{t('Upgrade to Max (100 videos)')}</span>
                </button>
                <a
                  href="https://wa.me/212691154252?text=Hi!%20I'm%20interested%20in%20a%20custom%20plan%20with%20higher%20video%20limits."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-6 py-3 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#22c55e] transition-all flex items-center justify-center gap-3"
                  onClick={() => setShowLimitReached(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.14 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span>{t('Contact Us on WhatsApp')}</span>
                </a>
                <button
                  onClick={() => setShowLimitReached(false)}
                  className="w-full px-6 py-3 border-2 border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all"
                >
                  {t('Maybe Later')}
                </button>
              </div>
            </>
          )}
          
          {/* Max users - Contact us for custom plans */}
          {userPlan === 'max' && (
            <>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('Monthly Limit Reached')}</h3>
              <p className="text-slate-500 mb-6">
                {t("You've used all {count} videos for this month. Need more? Let's talk about a custom plan!", { count: usageData?.limits?.videos_per_month || 100 })}
              </p>
              <div className="space-y-3">
                <a
                  href="https://wa.me/212691154252?text=Hi!%20I'm%20interested%20in%20a%20custom%20plan%20with%20higher%20video%20limits."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-6 py-3 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#22c55e] transition-all flex items-center justify-center gap-3"
                  onClick={() => setShowLimitReached(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span>{t('Contact Us on WhatsApp')}</span>
                </a>
                <button
                  onClick={() => setShowLimitReached(false)}
                  className="w-full px-6 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                >
                  {t('Got It')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Plan helpers
  const formatDuration = (seconds) => `${Math.floor(seconds / 60)} min`;

  return (
    <div className="flex h-[calc(100dvh-var(--welcome-banner-height,0px))] bg-[#f8fafc] text-slate-900 font-sans">
      <aside className="hidden lg:flex w-[280px] bg-white border-e border-slate-100 flex-col overflow-y-auto">
        <div className="h-20 shrink-0 flex items-center justify-center px-6">
          <div className="flex items-center justify-center">
            <img
              src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
              alt="ArabCap"
              className="h-12 w-auto"
            />
          </div>
        </div>
        <div className="flex-1 px-4 py-6 space-y-1.5">

          <button className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 font-bold text-[14px] bg-[#f73140]/5 text-[#f73140] relative overflow-hidden w-full text-start">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
            {t('Dashboard')}
            <span className="absolute start-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#f73140] rounded-e-full" />
          </button>
          <div className="pt-4 mt-4 border-t border-slate-100">
            <p className="px-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">{t('Coming Soon')}</p>
            
            <div className="group flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50/50 cursor-not-allowed w-full">
              <div className="flex items-center gap-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-300">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <span className="font-bold text-[14px] text-slate-400">{t('Folders')}</span>
              </div>
              <span className="text-[9px] font-bold bg-gradient-to-r from-violet-500 to-purple-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">{t('Soon')}</span>
            </div>

            <div className="group flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50/50 cursor-not-allowed w-full mt-1.5">
              <div className="flex items-center gap-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-300">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
                <span className="font-bold text-[14px] text-slate-400">{t('Brand Kit')}</span>
              </div>
              <span className="text-[9px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">{t('Soon')}</span>
            </div>


          </div>
        </div>
        <div className="p-4 mt-auto space-y-4">
          {showTimeSaved && (
            <TimeSavedCard videoSeconds={timeSaved.video_seconds} videos={timeSaved.completed_videos} />
          )}
          <div data-tour="usage-info" className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-5 text-white shadow-xl shadow-slate-900/10 relative overflow-hidden">
            {/* Plan badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className={`w-2 h-2 rounded-full ${
                usageData?.has_override ? 'bg-amber-400' : 
                userPlan === 'max' ? 'bg-purple-600' :
                userPlan === 'pro' ? 'bg-[#f73140]' : 
                userPlan === 'starter' ? 'bg-blue-500' : 
                'bg-slate-400'
              }`} />
              <span className="text-xs font-bold text-slate-300 uppercase tracking-wide">
                {t(usageData?.has_override ? 'Custom Plan' : 
                 userPlan === 'max' ? 'Max Plan' :
                 userPlan === 'pro' ? 'Pro Plan' : 
                 userPlan === 'starter' ? 'Starter Plan' : 
                 'Trial')}
              </span>
            </div>

            {/* Bonus videos banner - shown for all plans when there are bonus videos */}
            {usageData?.bonus_videos > 0 && (
              <div className="mb-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-3 border border-amber-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-400">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-amber-400">{t('{count} Bonus Videos', { count: usageData.bonus_videos })}</p>
                      <p className={`text-[9px] ${bonusExpiryNote ? 'font-semibold text-amber-300' : 'text-amber-400/70'}`}>{bonusExpiryNote || t('Used first before plan quota')}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Usage stats */}
            {userPlan === 'trial' ? (
              <div className="mb-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-white" dir="ltr">
                    {usageData?.trial_videos_remaining ?? 1}
                    <span className="text-lg font-bold text-slate-400"> / {usageData?.trial_videos_total ?? 1}</span>
                  </p>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mt-1">
                    {t('Trial Videos (Lifetime)')}
                  </p>
                </div>
                <p className="text-center text-xs text-slate-400 mt-2">{t('2 min max per video')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-white" dir="ltr">
                    {usageData?.limits?.videos_remaining ?? 0}
                    <span className="text-sm font-bold text-slate-400">/{usageData?.limits?.videos_per_month ?? (userPlan === 'max' ? '100' : userPlan === 'pro' ? '30' : '8')}</span>
                  </p>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{t('Remaining')}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-white">
                    {usageData?.limits?.max_duration_seconds ? Math.floor(usageData.limits.max_duration_seconds / 60) : '2'} 
                    <span className="text-sm font-bold text-slate-400"> {t('min')}</span>
                  </p>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{t('Max / video')}</p>
                </div>
              </div>
            )}

            {/* Progress bar - only for monthly plans */}
            {userPlan !== 'trial' && (
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-[10px] font-bold text-slate-400">
                    {t('{used} of {total} used', { used: usageData?.limits?.videos_used ?? 0, total: usageData?.limits?.videos_per_month ?? (userPlan === 'max' ? '100' : userPlan === 'pro' ? '30' : '8') })}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400">
                    {usageData?.limits?.videos_per_month ? Math.round((usageData.limits.videos_used / usageData.limits.videos_per_month) * 100) : 0}%
                  </span>
                </div>
                <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${
                      userPlan === 'max' ? 'bg-purple-600' :
                      userPlan === 'pro' ? 'bg-[#f73140]' : 
                      userPlan === 'starter' ? 'bg-blue-500' : 
                      'bg-slate-400'
                    } rounded-full transition-all`} 
                    style={{ width: `${usageData?.limits?.videos_per_month ? Math.min((usageData.limits.videos_used / usageData.limits.videos_per_month) * 100, 100) : 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Renewal info */}
            <p className="text-[10px] text-slate-500 font-medium">
              {userPlan === 'trial' 
                ? t('Trial credits never expire') 
                : (formatRenewalDate(usageData?.override_expires_at) || getResetDate())}
            </p>

            {/* Upgrade button for trial, starter, and pro users */}
            {(userPlan === 'trial' || userPlan === 'starter' || userPlan === 'pro') && !usageData?.has_override && (
              <button 
                onClick={() => setShowUpgradeModal(true)}
                className={`mt-4 flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all ${
                  userPlan === 'pro' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#f73140] hover:bg-[#f73140]/90'
                }`}
              >
                <span>{t(userPlan === 'pro' ? 'Upgrade to Max' : userPlan === 'starter' ? 'Upgrade to Pro' : 'Upgrade Now')}</span>
              </button>
            )}
          </div>
          <button 
            onClick={() => navigate('/settings')}
            data-tour="settings-button"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-sm font-bold text-slate-500 hover:text-[#f73140] hover:bg-slate-50 transition-colors text-start border border-slate-100"
          >
            <span className="w-9 h-9 rounded-full bg-white border border-slate-100 text-slate-500 flex items-center justify-center shadow-sm">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.82-.33 1.7 1.7 0 0 0-1 1.54V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1-1.54 1.7 1.7 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .33-1.82 1.7 1.7 0 0 0-1.54-1H3a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.54-1 1.7 1.7 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.82.33H9a1.7 1.7 0 0 0 1-1.54V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1 1.54 1.7 1.7 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.33 1.82V9c0 .69.4 1.31 1.02 1.58.24.1.5.16.78.16H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.26z" />
              </svg>
            </span>
            <div className="flex flex-col">
              <span className="text-slate-700">{t('Settings')}</span>
              <span className="text-[11px] font-semibold text-slate-400">{t('Profile, billing, preferences')}</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {(sidebarOpen || sidebarClosing) && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
              sidebarAnimating && !sidebarClosing ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={handleCloseSidebar}
          />
          <aside className={`absolute start-0 top-0 h-full w-[280px] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out ${
            sidebarAnimating && !sidebarClosing ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
          }`}>
            <div className="h-20 flex items-center justify-between px-6">
              <img
                src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
                alt="ArabCap"
                className="h-12 w-auto"
              />
              <button
                onClick={handleCloseSidebar}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <XIcon size={20} className="text-slate-500" />
              </button>
            </div>
            <div className="flex-1 px-4 py-6 space-y-1.5">
              <button className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 font-bold text-[14px] bg-[#f73140]/5 text-[#f73140] relative overflow-hidden w-full text-start">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
                {t('Dashboard')}
                <span className="absolute start-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#f73140] rounded-e-full" />
              </button>
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="px-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">{t('Coming Soon')}</p>
                
                <div className="group flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50/50 cursor-not-allowed w-full">
                  <div className="flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-300">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    <span className="font-bold text-[14px] text-slate-400">{t('Folders')}</span>
                  </div>
                  <span className="text-[9px] font-bold bg-gradient-to-r from-violet-500 to-purple-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">{t('Soon')}</span>
                </div>

                <div className="group flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50/50 cursor-not-allowed w-full mt-1.5">
                  <div className="flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-300">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                      <line x1="9" y1="9" x2="9.01" y2="9" />
                      <line x1="15" y1="9" x2="15.01" y2="9" />
                    </svg>
                    <span className="font-bold text-[14px] text-slate-400">{t('Brand Kit')}</span>
                  </div>
                  <span className="text-[9px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">{t('Soon')}</span>
                </div>

              </div>
            </div>
            <div className="p-4 mt-auto space-y-4">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-5 text-white shadow-xl shadow-slate-900/10 relative overflow-hidden">
                {/* Plan badge */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={`w-2 h-2 rounded-full ${usageData?.has_override ? 'bg-amber-400' : (userPlan === 'max' ? 'bg-purple-600' : userPlan === 'pro' ? 'bg-[#f73140]' : userPlan === 'starter' ? 'bg-emerald-500' : 'bg-slate-400')}`} />
                  <span className="text-xs font-bold text-slate-300 uppercase tracking-wide">
                    {t(usageData?.has_override ? 'Custom Plan' : (userPlan === 'max' ? 'Max Plan' : userPlan === 'pro' ? 'Pro Plan' : userPlan === 'starter' ? 'Starter Plan' : 'Trial'))}
                  </span>
                </div>

                {/* Bonus videos banner - shown when there are bonus videos */}
                {usageData?.bonus_videos > 0 && (
                  <div className="mb-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-2.5 border border-amber-500/30">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-400">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-amber-400">{t('{count} Bonus Videos', { count: usageData.bonus_videos })}</p>
                        <p className={`text-[8px] ${bonusExpiryNote ? 'font-semibold text-amber-300' : 'text-amber-400/70'}`}>{bonusExpiryNote || t('Used first before plan quota')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Usage stats */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-white">{usageData?.limits?.videos_per_month ?? (userPlan === 'max' ? '100' : userPlan === 'pro' ? '30' : userPlan === 'starter' ? '8' : '3')}</p>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{t('Videos')}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-white">{usageData?.limits?.max_duration_seconds ? Math.floor(usageData.limits.max_duration_seconds / 60) : '2'} <span className="text-sm font-bold text-slate-400">{t('min')}</span></p>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{t('Max / video')}</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-[10px] font-bold text-slate-400">{t('{used} of {total} used', { used: usageData?.limits?.videos_used ?? 0, total: usageData?.limits?.videos_per_month ?? (userPlan === 'max' ? '100' : userPlan === 'pro' ? '30' : userPlan === 'starter' ? '8' : '3') })}</span>
                    <span className="text-[10px] font-bold text-slate-400">{usageData?.limits?.videos_per_month ? Math.round((usageData.limits.videos_used / usageData.limits.videos_per_month) * 100) : 0}%</span>
                  </div>
                  <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${userPlan === 'max' ? 'bg-purple-600' : userPlan === 'pro' ? 'bg-[#f73140]' : userPlan === 'starter' ? 'bg-emerald-500' : 'bg-slate-400'} rounded-full transition-all`} 
                      style={{ width: `${usageData?.limits?.videos_per_month ? Math.min((usageData.limits.videos_used / usageData.limits.videos_per_month) * 100, 100) : 0}%` }}
                    />
                  </div>
                </div>

                {/* Renewal info */}
                <p className="text-[10px] text-slate-500 font-medium">
                  {formatRenewalDate(usageData?.override_expires_at) || getResetDate()}
                </p>

                {/* Upgrade button for trial users (hide if has custom override) */}
                {(userPlan === 'trial' || userPlan === 'starter' || userPlan === 'pro') && !usageData?.has_override && (
                  <button 
                    onClick={() => {
                      setShowUpgradeModal(true);
                      handleCloseSidebar();
                    }}
                    className={`mt-4 flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all ${
                      userPlan === 'pro' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#f73140] hover:bg-[#f73140]/90'
                    }`}
                  >
                    <span>{t(userPlan === 'pro' ? 'Upgrade to Max' : userPlan === 'starter' ? 'Upgrade to Pro' : 'Subscribe Now')}</span>
                  </button>
                )}
              </div>
              <button 
                onClick={() => {
                  navigate('/settings');
                  handleCloseSidebar();
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-sm font-bold text-slate-500 hover:text-[#f73140] hover:bg-slate-50 transition-colors text-start border border-slate-100"
              >
                <span className="w-9 h-9 rounded-full bg-white border border-slate-100 text-slate-500 flex items-center justify-center shadow-sm">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.7 1.7 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.82-.33 1.7 1.7 0 0 0-1 1.54V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1-1.54 1.7 1.7 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .33-1.82 1.7 1.7 0 0 0-1.54-1H3a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.54-1 1.7 1.7 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.82.33H9a1.7 1.7 0 0 0 1-1.54V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1 1.54 1.7 1.7 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.33 1.82V9c0 .69.4 1.31 1.02 1.58.24.1.5.16.78.16H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.26z" />
                  </svg>
                </span>
                <div className="flex flex-col">
                  <span className="text-slate-700">{t('Settings')}</span>
                  <span className="text-[11px] font-semibold text-slate-400">{t('Profile, billing, preferences')}</span>
                </div>
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-y-auto relative" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label={t('Open navigation menu')}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-700">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <img
            src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
            alt="ArabCap"
            className="h-8 w-auto"
          />
          <LanguageSwitcher compact />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 space-y-4 sm:space-y-6 md:space-y-8 pb-20">
          {modalContent}
          {manifestoModal}
          {limitReachedModal}

        <PremiereDashboardCard />

        {/* Mobile / tablet: big banner (desktop shows it in the sidebar) */}
        {showTimeSaved && (
          <div className="lg:hidden">
            <TimeSavedCard videoSeconds={timeSaved.video_seconds} videos={timeSaved.completed_videos} />
          </div>
        )}

        <PartnerOffersBanner geoCountry={geoCountry} />

        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96 group">
            <span className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#f73140] transition-colors">
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder={t('Search projects...')}
              className="w-full ps-12 pe-4 py-3.5 bg-white border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-[#f73140]/20 transition-all placeholder-slate-400 shadow-sm"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <LanguageSwitcher className="hidden lg:inline-flex h-12 rounded-2xl px-4" />
            <TourTriggerButton onClick={() => setShowTour(true)} />
            <button
              onClick={openNewProjectModal}
              data-tour="new-project-button"
              className="w-full md:w-auto rounded-2xl px-8 py-3 bg-[#f73140] text-white font-bold shadow-lg shadow-[#f73140]/20 hover:bg-[#d62331] transition-all flex items-center justify-center gap-2"
            >
              <PlusIcon /> {t('New project')}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {isLoadingJobs ? (
            /* Only show skeleton when loading */
            <ProjectGridSkeleton count={8} />
          ) : (
            <>
              {jobsError && (
                <div className="bg-red-50 border border-red-100 rounded-3xl p-6 text-center text-red-600">
                  <p className="text-sm font-semibold">{t(jobsError)}</p>
                </div>
              )}
              
              {filteredProjects.length > 0 && (
                <div className="px-2">
                  <h3 className="text-xl font-bold text-slate-900">{t('Your projects ({count})', { count: totalRecords })}</h3>
                </div>
              )}

              <div key={animKey} data-tour="project-grid" className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-6 animate-slide-in">
                {currentProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onOpen={() => navigate(`/editor/${project.id}`)} />
                ))}
                {(currentPage === totalPages || filteredProjects.length === 0) && (
                  <button
                    type="button"
                    onClick={openNewProjectModal}
                    className="border-2 border-dashed border-slate-200 rounded-[20px] md:rounded-[24px] flex flex-col items-center justify-center min-h-[150px] md:min-h-[280px] cursor-pointer hover:border-[#f73140] hover:bg-[#f73140]/5 transition-all duration-300 group bg-white/50 hover:shadow-xl hover:shadow-[#f73140]/5"
                  >
                    <div className="w-8 h-8 md:w-16 md:h-16 rounded-full bg-white shadow-sm border border-slate-100 group-hover:scale-110 group-hover:border-[#f73140]/20 flex items-center justify-center mb-2 md:mb-4 transition-all duration-300">
                      <PlusIcon />
                    </div>
                    <span className="font-bold text-slate-900 text-xs md:text-lg text-center px-1">{t('Create a project')}</span>
                    <span className="text-[9px] md:text-xs font-semibold text-slate-400 mt-0.5 md:mt-1 uppercase tracking-wide">{t('Blank')}</span>
                  </button>
                )}
              </div>
            </>
          )}

          {!isLoadingJobs && totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8 pt-4">
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className="p-3 rounded-full bg-white text-slate-500 shadow-sm border border-slate-100 hover:bg-slate-50 hover:text-[#f73140] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isRTL ? '›' : '‹'}
              </button>
              <span className="text-sm font-bold text-slate-500">
                {rich(t('Page <b>{page}</b> of {total}', { page: currentPage, total: totalPages }), { b: (c) => <span className="text-slate-900">{c}</span> })}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-3 rounded-full bg-white text-slate-500 shadow-sm border border-slate-100 hover:bg-slate-50 hover:text-[#f73140] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isRTL ? '‹' : '›'}
              </button>
            </div>
          )}
        </div>
        </div>
      </main>

      {/* WhatsApp Floating Button */}
      <WhatsAppFloatingButton />

      {/* Hidden Audio Element */}
      {previewAudio && (
        <audio
          ref={audioRef}
          src={previewAudio.audioUrl}
          onTimeUpdate={handleAudioTimeUpdate}
          onLoadedMetadata={handleAudioLoadedMetadata}
          onEnded={handleAudioEnded}
        />
      )}

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        onPhoneSubmit={handlePhoneSubmit}
        onSkip={() => setShowOnboarding(false)}
        isSkippable={false}
      />

      {/* Dashboard Tour */}
      <DashboardTour
        isOpen={showTour}
        onComplete={() => setShowTour(false)}
        onSkip={() => setShowTour(false)}
        onNewProjectClick={handleTourNewProjectClick}
      />

      {/* Project Creation Guide - shows alongside modal */}
      <ProjectCreationTour
        isOpen={isModalOpen && showProjectTour && modalStep <= 6 && modalStep !== lastDismissedStep}
        modalStep={modalStep}
        onClose={() => {
          // Only dismiss for current step - will show again on next step
          setLastDismissedStep(modalStep);
        }}
      />

      {/* Upgrade Modal */}
      {showUpgradeModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-[#f5f5f5] rounded-2xl sm:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto relative">
            {/* Close button - subtle */}
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="absolute top-3 end-3 z-10 w-7 h-7 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 flex items-center justify-center transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Modal Header */}
            <div className="text-center pt-6 sm:pt-8 pb-3 sm:pb-4 px-4 sm:px-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900">{t('Our Pricing Options')}</h2>
            </div>

            {/* Modal Content */}
            <div className="px-3 sm:px-6 pb-6 sm:pb-8">
              {/* Special Limited Offer Banner with FOMO */}
              <div className="mb-4 sm:mb-6 relative">
                <div className="bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-5 relative overflow-hidden">
                  {/* Animated gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#f73140]/20 via-purple-500/20 to-[#f73140]/20 animate-pulse" />
                  
                  <div className="relative flex flex-col items-center text-center gap-3">
                    {/* Live indicator + Badge row */}
                    <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                      {/* Live viewers indicator */}
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/10 rounded-full">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-[10px] sm:text-xs text-slate-300 font-medium">{t('12 people viewing')}</span>
                      </div>
                      
                      {/* Limited time badge */}
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#f73140] to-orange-500 rounded-full">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-white">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span className="text-[10px] sm:text-xs font-bold text-white uppercase tracking-wider">{t('Ends Soon')}</span>
                      </div>
                    </div>
                    
                    {/* Main text */}
                    <div>
                      <h3 className="text-lg sm:text-xl font-black text-white mb-1">
                        {t('Special Launch Prices')}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-400">
                        {t('Lock in these exclusive rates before they increase')}
                      </p>
                    </div>
                    
                    {/* FOMO Stats Row */}
                    <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 pt-2 border-t border-white/10 w-full">
                      {/* Recent signups */}
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-2 rtl:space-x-reverse">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-slate-900 flex items-center justify-center text-[8px] font-bold text-white">A</div>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-slate-900 flex items-center justify-center text-[8px] font-bold text-white">M</div>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 border-2 border-slate-900 flex items-center justify-center text-[8px] font-bold text-white">S</div>
                        </div>
                        <span className="text-[10px] sm:text-xs text-slate-400">
                          {rich(t('<b>47 creators</b> joined this week'), { b: (c) => <span className="text-white font-semibold">{c}</span> })}
                        </span>
                      </div>
                      
                      {/* Spots indicator */}
                      <div className="flex items-center gap-1.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2" className="flex-shrink-0">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <span className="text-[10px] sm:text-xs text-slate-400">
                          {rich(t('Only <b>8 spots</b> left at this price'), { b: (c) => <span className="text-[#f73140] font-bold">{c}</span> })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plans Grid - Stack on mobile, 3 columns on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                {/* Starter Plan */}
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-1 sm:mb-2">STARTER</h3>
                    <p className="text-xs sm:text-sm text-slate-500">{t('Perfect for beginner video editors who are just getting started.')}</p>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('8 videos')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t(planPrices.starter.perVideo)}</span>
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('Email support')}</span>
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <div className="text-center mb-3 sm:mb-4">
                      <span className="text-3xl sm:text-4xl font-black text-slate-900">{t(planPrices.starter.display)}</span>
                      <span className="text-slate-500 text-xs sm:text-sm">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('starter')}
                      disabled={upgradeLoading || userPlan === 'starter'}
                      className={`w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all ${
                        userPlan === 'starter'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'starter' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GET STARTED')}
                    </button>
                  </div>
                </div>

                {/* Pro Plan - Featured */}
                <div className="bg-[#f73140] rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg flex flex-col relative order-first md:order-none">
                  {/* Popular badge */}
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-slate-900 text-white text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wide">{t('Popular')}</span>
                  </div>
                  
                  <div className="mb-3 sm:mb-4 mt-2 sm:mt-0">
                    <h3 className="text-lg sm:text-xl font-black text-white mb-1 sm:mb-2">PRO</h3>
                    <p className="text-xs sm:text-sm text-white/80">{t('Great for active editors working with more clients and content.')}</p>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                    <li className="flex items-center gap-2 text-white">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('30 videos')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t(planPrices.pro.perVideo)}</span>
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('Priority support')}</span>
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <div className="text-center mb-3 sm:mb-4">
                      <span className="text-3xl sm:text-4xl font-black text-white">{<WelcomePrice plan="pro" price={planPrices.pro} />}</span>
                      <span className="text-white/80 text-xs sm:text-sm">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('pro')}
                      disabled={upgradeLoading || userPlan === 'pro'}
                      className={`w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all ${
                        userPlan === 'pro'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-white text-[#f73140] hover:bg-slate-50 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'pro' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GO PRO')}
                    </button>
                  </div>
                </div>

                {/* Max Plan */}
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-1 sm:mb-2">MAX</h3>
                    <p className="text-xs sm:text-sm text-slate-500">{t('Built for professional editors handling large content demands.')}</p>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('100 videos')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t(planPrices.max.perVideo)}</span>
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="flex-shrink-0">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-xs sm:text-sm">{t('Dedicated support')}</span>
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <div className="text-center mb-3 sm:mb-4">
                      <span className="text-3xl sm:text-4xl font-black text-slate-900">{<WelcomePrice plan="max" price={planPrices.max} />}</span>
                      <span className="text-slate-500 text-xs sm:text-sm">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('max')}
                      disabled={upgradeLoading || userPlan === 'max'}
                      className={`w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all ${
                        userPlan === 'max'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'max' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GET PREMIUM')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {billingError && (
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl bg-red-50 border border-red-100">
                  <p className="text-xs sm:text-sm text-red-600 text-center">{t(billingError)}</p>
                </div>
              )}

              {/* Benefits Footer */}
              <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mt-5 sm:mt-8">
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="w-4 h-4 sm:w-5 sm:h-5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{t('Cancel anytime')}</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="w-4 h-4 sm:w-5 sm:h-5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{t('Customer support')}</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2.5" className="w-4 h-4 sm:w-5 sm:h-5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{t('1 video free trial')}</span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="text-center mt-4 sm:mt-6">
                <p className="text-xs sm:text-sm text-slate-600">
                  {rich(t('Contact us on WhatsApp at <a>+212 691 154 252</a> for a custom offer.'), {
                    a: (c) => <a href="https://wa.me/212691154252" target="_blank" rel="noopener noreferrer" dir="ltr" className="font-bold text-slate-900 hover:text-[#f73140] transition-colors">{c}</a>,
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Phone verification (fraud check at upload); portaled so it sits above the project modal */}
      {showVerificationModal && createPortal(
        <PhoneVerificationModal
          isOpen={showVerificationModal}
          onClose={() => {
            retryAfterVerificationRef.current = false;
            setShowVerificationModal(false);
          }}
          onVerified={handlePhoneVerified}
          onUpgrade={() => {
            retryAfterVerificationRef.current = false;
            setShowVerificationModal(false);
            setShowUpgradeModal(true);
          }}
          phoneNumber={user?.phone_number}
        />,
        document.body
      )}
    </div>
  );
};

export default Dashboard;
